import { articlesAReapprovisionner } from '../../lib/valeur-stock';
import { useRef, useState } from 'react';
import { ArticleMedia } from '../../components/ArticleMedia.jsx';
import { ScanCodeBarres } from '../../components/ScanCodeBarres.jsx';
import { useDemande } from '../../components/Dialogue.jsx';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { Inp, NumInput, inpFS } from '../../components/ui/Inputs.jsx';
import { Pill } from '../../components/ui/Pill.jsx';
import { StatBox } from '../../components/ui/StatBox.jsx';
import { TauxTvaArticle } from '../../components/ui/TauxTvaArticle.jsx';
import { Tabs } from '../../components/ui/Tabs.jsx';
import { SOC } from '../../constants/company';
import { TRADE_PRESETS, metierRayons, metierUnites } from '../../constants/trades';
import { ScanFacture } from '../../components/ScanFacture.jsx';
import { DEVISE, fmt } from '../../lib/currency';
import { achatDepuisFacture } from '../../lib/facture-fournisseur';
import { remiseClient } from '../../lib/discounts';
import { DELAIS_PROPOSES, delaiSaisi, libelleDelai } from '../../lib/echeances';
import { tr } from '../../lib/i18n';
import { COND_TYPES, condBase } from '../../lib/packaging';
import { printDoc } from '../../lib/print';
import { ouvreDocument } from '../../api/documents';
import { PleinEcran } from '../../components/ui/PleinEcran.jsx';
import { BoutonHaut } from '../../components/ui/BoutonHaut.jsx';
import { tauxTvaEnVigueur } from '../../lib/tva';
import {
  MOYENS,
  clotureCaisse,
  libelleTaux,
  numeroTicket,
  quantitesRestantes,
  ticketsDe,
} from '../../lib/caisse';
import { auCentime, prixCatalogue } from '../../lib/vat';
import { idProvisoire, prochaineReference } from '../../lib/identifiants';
import { today, uid } from '../../lib/utils';
import { ordreDeSaisie } from '../../lib/ordre';
import { ACCESS_PERMS, ACCESS_ROLE, roleCan, voitLeBenefice } from '../../lib/access';
import { prepareLeJustificatif } from '../../lib/justificatif';
import { C } from '../../theme/palette';
import { adresseApi } from '../../api/client';
import { articleVersDto, dtoVersVente, venteVersDto } from '../../api/mappers';
import { useReglages } from '../../api/reglages.jsx';
import { chantiersApi, stockApi, ventesApi } from '../../api/resources';
import { useSession } from '../../api/session.jsx';
import { AjoutEnLot } from './AjoutEnLot.jsx';
import { AvoirComptoir } from './AvoirComptoir.jsx';
import { ImportCatalogue } from './ImportCatalogue.jsx';
import { CatalogueStock } from './CatalogueStock.jsx';
import { Inventaire } from './Inventaire.jsx';
import { TicketComptoir } from './TicketComptoir.jsx';
import { EnvoiCatalogue, EnvoiCatalogueNeutre } from './EnvoiCatalogue.jsx';

/**
 * Adresse de la vignette d'un article : sa premiere photo.
 *
 * Le plan n'en fait pas office — un PDF ne s'affiche pas dans une balise
 * d'image, et le rayon montrerait un cadre vide.
 *
 * @param {object} art article de stock
 * @returns {string} l'adresse de la photo, ou une chaine vide
 */
function vignetteDe(art) {
  const photo = (art.medias || []).find((m) => m && m.typePiece !== 'PLAN' && m.fichier);
  return photo ? photo.fichier : '';
}

/**
 * Case d'une remise en %, avec décimales. Le chiffre ne s'enregistre qu'en
 * quittant la case : enregistré à chaque touche, « 12, » devenait 12 puis
 * « 125 », plafonné à 100 % — et « 7.5 » devenait 75 %.
 */
function CaseRemise({ valeur, onValide, ...reste }) {
  const [texte, setTexte] = useState(null);
  return (
    <input
      {...reste}
      inputMode="decimal"
      value={texte ?? (valeur === '' || valeur == null ? '' : String(valeur))}
      onChange={(e) => setTexte(e.target.value)}
      onBlur={() => {
        if (texte != null) onValide(texte);
        setTexte(null);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
    />
  );
}

export function StockPage({
  onNav,
  soc,
  stock,
  setStock,
  catalogue,
  ventes,
  setVentes,
  clients,
  setClients,
  initTab,
  initAdd,
  estMixte,
  univers,
  setUnivers,
  setFrais,
  rechargeStock,
  chantiers,
  rechargeChantiers,
}) {
  const { reglage, setReglage } = useReglages();
  const demande = useDemande();
  const [tab, setTab] = useState(initTab || 'stock');
  const [artAdd, setArtAdd] = useState(!!initAdd);
  const [bulk, setBulk] = useState(false);
  const [presetMet, setPresetMet] = useState('');
  const [artEdit, setArtEdit] = useState(null);
  const [reappro, setReappro] = useState(null);
  const [sortie, setSortie] = useState(null);
  const [artNewMedias, setArtNewMedias] = useState([]);
  const [artNewEnvoi, setArtNewEnvoi] = useState(false);
  const [artNewErreur, setArtNewErreur] = useState('');
  const [artNewDetails, setArtNewDetails] = useState(false);
  const [outilsArt, setOutilsArt] = useState(null);
  const [artNew, setArtNew] = useState({
    nom: '',
    cat: '',
    u: (estMixte ? univers === 'magasin' : soc?.secteur === 'magasin') ? 'pièce' : 'pce',
    pv: 0,
    pa: 0,
    // Nul vaut « taux de la societe » : un changement de taux legal se fait
    // alors en un seul endroit, sans reprendre chaque article.
    tva: null,
    stock: 0,
    min: 5,
    cond: '',
    condQte: '',
    condPar: '',
    conds: [],
    codeBarre: '',
  });
  const [selCat, setSelCat] = useState('');
  // Le taux de TVA du magasin, etabli une fois pour l'ecran entier : le taux
  // normal en vigueur aujourd'hui (Reglages → Documents → TVA), puisqu'on
  // encaisse aujourd'hui. Les prix du catalogue sont hors taxe : ce taux sert
  // a ajouter la TVA au moment d'encaisser, jamais a l'extraire d'un prix qui
  // ne la porte pas.
  const tauxCaisse = tauxTvaEnVigueur(soc).normal;
  // Le taux d'un article (réduit pour une denrée), sinon celui de la caisse.
  const tauxDeLArticle = (a) => (a?.tva != null && a.tva !== '' ? +a.tva : tauxCaisse);
  // La cloture du jour, calculee une fois pour la vignette et pour le rapport
  // imprime : les deux la refaisaient chacun de leur cote.
  const ventesDuJour = ventes.filter((v) => v.date === today());
  const clotureDuJour = clotureCaisse(ventesDuJour, tauxCaisse);
  const [showVente, setShowVente] = useState(false);
  // Clients dans l'ordre de saisie, comme la maquette : le premier saisi
  // est proposé d'office aux remises. La caisse, elle, part sur « Client de
  // passage » : présélectionner le premier client lui appliquait ses remises
  // et lui attribuait les ventes au comptoir de n'importe qui.
  const clientsTri = ordreDeSaisie(clients);
  const [vClient, setVClient] = useState(0);
  const [vPay, setVPay] = useState('especes');
  const [vRecu, setVRecu] = useState('');
  const [ticket, setTicket] = useState(null);
  // Le ticket dont le client rapporte tout ou partie, le temps de l'avoir.
  const [avoirDe, setAvoirDe] = useState(null);
  const [vRemiseGlob, setVRemiseGlob] = useState('');
  const [showZ, setShowZ] = useState(false);
  const session = useSession()?.session || null;
  // L'inventaire suit le reglage « acces stock » : la route des ventes mene ici
  // sans lui, et l'onglet ne doit pas ouvrir ce que la route du stock ferme.
  const voitInventaire = roleCan('stock');
  // Les prix d'achat ne se montrent qu'a qui lit deja les couts : la direction,
  // la comptabilite, et qui a recu le droit de voir le benefice.
  const voitPrixAchat =
    ACCESS_ROLE === 'patron' ||
    ACCESS_ROLE === 'comptable' ||
    !!(ACCESS_PERMS[ACCESS_ROLE] && ACCESS_PERMS[ACCESS_ROLE].benefice);

  /**
   * Enregistre un avoir : chaque ligne part au serveur, qui la borne sur le
   * ticket d'origine et rend l'article au stock. On ne passe pas par la salve
   * differee : un refus (409) doit se lire tout de suite, a la caisse, et non
   * apres coup dans un bandeau.
   */
  const enregistreAvoir = async (lignes, choix) => {
    const crees = [];
    for (const l of lignes) {
      const dto = await ventesApi.cree(venteVersDto(l));
      crees.push(dtoVersVente(dto));
    }
    setVentes((p) => [...crees, ...p]);
    // Le serveur a rendu la quantite au rayon : on relit l'article plutot que
    // de l'additionner ici, pour ne pas la compter deux fois.
    const refs = Array.from(new Set(crees.map((v) => v.ref).filter(Boolean)));
    for (const ref of refs) {
      const art = stock.find((a) => a.ref === ref);
      if (!art || typeof art.id !== 'number') continue;
      try {
        const dto = await stockApi.parId(art.id);
        setStock((p) =>
          p.map((m) => (m.id === art.id ? { ...m, stock: dto.quantiteStock ?? m.stock } : m)),
        );
      } catch (e) {}
    }
    const totaux = clotureCaisse(crees, tauxCaisse);
    const origine = avoirDe;
    const cl = clients.find((c) => c.id === origine?.clientId);
    setTicket({
      avoir: true,
      num: crees[0]?.ticket || '',
      ticketOrigine: origine?.ticket || '',
      motif: choix?.motif || '',
      date: today(),
      heure: new Date().toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' }),
      clientNom: cl ? cl.nom : 'Client de passage',
      lignes: crees.map((v) => ({ nom: v.nom, q: v.q, pv: v.pv, rem: 0, net: v.total })),
      remiseGlob: 0,
      totalArt: totaux.ht,
      total: totaux.ttc,
      totalHt: totaux.ht,
      tvaTaux: tauxCaisse,
      tva: totaux.tva,
      ht: totaux.ht,
      ventilation: totaux.ventilation,
      pay: choix?.mode || 'especes',
      recu: 0,
      rendu: 0,
      pointsSolde: null,
    });
    setAvoirDe(null);
  };
  const [panier, setPanier] = useState([]);
  const [scanInconnu, setScanInconnu] = useState('');
  /** Code tapé, lu à la douchette ou par la caméra : une unité de plus au panier. */
  const ajouterParCode = (brut) => {
    const code = String(brut || '').trim();
    if (!code) return;
    const a = stock.find((x) => (x.codeBarre || '').trim() === code);
    if (!a) {
      setScanInconnu(code);
      return;
    }
    setScanInconnu('');
    setPanier((p) =>
      p.find((x) => x.artId === a.id)
        ? p.map((x) => (x.artId === a.id ? { ...x, q: x.q + 1 } : x))
        : [...p, { artId: a.id, q: 1, rem: 0 }],
    );
  };
  const [artQ, setArtQ] = useState('');
  const [remCli, setRemCli] = useState(clientsTri[0]?.id || '');
  const [qkPct, setQkPct] = useState('');
  const [qkScope, setQkScope] = useState('__all');
  const [remQ, setRemQ] = useState('');
  const [remAdd, setRemAdd] = useState(false);
  const [remSaved, setRemSaved] = useState(false);
  const [remAddCli, setRemAddCli] = useState(false);
  const [remNewName, setRemNewName] = useState('');
  const [remCat, setRemCat] = useState(false);
  // Ce qui défile dans le catalogue d'un client : le rond « ⬆ » y remonte.
  const remCatDefile = useRef(null);
  const [remCatPublic, setRemCatPublic] = useState(false);
  // Le catalogue envoyé au client peut montrer ses prix TVA comprise : un
  // particulier compare des prix TTC, un professionnel des prix hors taxe.
  const [catTTC, setCatTTC] = useState(false);
  // Le panneau « Envoyer le catalogue » à plusieurs personnes de la société.
  const [envoiCat, setEnvoiCat] = useState(false);
  const [grilleName, setGrilleName] = useState('');
  const [grilles, setGrilles] = useState(() => {
    try {
      return JSON.parse(reglage('grillesPrix') || '[]');
    } catch (e) {
      return [];
    }
  });
  const saveGrilles = (g) => {
    setGrilles(g);
    try {
      setReglage('grillesPrix', JSON.stringify(g));
    } catch (e) {}
  };
  const stockOnly = estMixte ? univers !== 'magasin' : soc?.secteur !== 'magasin';
  const remisePour = (cl, art) => remiseClient(cl, art?.ref);
  // Rayons et articles dans l'ordre de saisie, comme la maquette.
  const stockTri = ordreDeSaisie(stock);
  const cats = Array.from(new Set(stockTri.map((m) => m.cat)));
  const [showCat, setShowCat] = useState(false);
  const catsShown = selCat ? cats.filter((c) => c === selCat) : cats;
  // La barre du magasin appelle cet écran « Ventes » : aucun onglet ne
  // s'allumait avec « stock ».
  const active = (estMixte ? univers === 'magasin' : soc?.secteur === 'magasin')
    ? 'ventes'
    : 'plus';

  return (
    <Shell
      active={active}
      onNav={onNav}
      soc={soc}
      pageTitle={stockOnly ? '📦 Stock / Dépôt' : '🛒 Stock & ventes'}
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          display: 'grid',
          // Valeur du stock et chiffre des ventes : la direction seule (la
          // secrétaire voyait « CA ventes », et une valeur de stock à 0.00
          // faute des prix d'achat qui lui sont cachés).
          gridTemplateColumns: !voitLeBenefice() ? '1fr' : stockOnly ? '1fr 1fr' : '1fr 1fr 1fr',
          gap: 7,
          marginBottom: 12,
        }}
      >
        {voitLeBenefice() && (
          <StatBox
            label="Valeur stock"
            value={DEVISE + ' ' + fmt(stock.reduce((s, m) => s + m.stock * m.pa, 0))}
            color={C.navy}
          />
        )}
        {!stockOnly && voitLeBenefice() && (
          <StatBox
            label="CA ventes"
            value={DEVISE + ' ' + fmt(ventes.reduce((s, v) => s + v.total, 0))}
            color={C.green}
          />
        )}
        <StatBox
          label="Alertes"
          value={String(articlesAReapprovisionner(stock).length)}
          color={C.red}
        />
      </div>
      <Tabs
        items={[
          ...(stockOnly
            ? [['stock', '📦 Stock']]
            : [
                ['stock', '📦 Stock'],
                ['ventes', '🛒 Ventes'],
                // Le chiffre des ventes et la marge : la direction seule.
                ...(voitLeBenefice() ? [['marge', '📈 Marge']] : []),
                ['remises', '% Remises'],
              ]),
          ...(voitInventaire && stockOnly ? [['inventaire', '📋 Inventaire']] : []),
        ]}
        active={tab}
        onChange={setTab}
      />
      <div style={{ marginTop: 12 }} />

      {tab === 'inventaire' && voitInventaire && (
        <Inventaire
          stock={stock}
          setStock={setStock}
          soc={soc}
          voitPrixAchat={voitPrixAchat}
          utilisateur={session?.nom || ''}
        />
      )}

      {tab === 'stock' && (
        <div>
          {/*
            Importer le catalogue d'un fournisseur et partager le sien avec
            ses clients sont des gestes de magasin : le dépôt du bâtiment
            garde ses articles, leur valeur et leurs alertes de seuil.
          */}
          {!stockOnly && <ImportCatalogue onImporte={rechargeStock} />}
          {!stockOnly && (stock || []).length > 0 && (
            <div
              onClick={() => setShowCat(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 7,
                background: C.tealL,
                border: '1px solid ' + C.teal + '44',
                borderRadius: 11,
                padding: 12,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 800,
                color: C.teal,
                marginBottom: 11,
              }}
            >
              📤 Partager mon catalogue{' '}
              <span style={{ fontSize: 9, fontWeight: 600, opacity: 0.8 }}>(prix hors TVA)</span>
            </div>
          )}
          {showCat && (
            <CatalogueStock
              stock={stockTri}
              soc={soc}
              tauxDefaut={tauxCaisse}
              vignette={vignetteDe}
              clients={clientsTri}
              setClients={setClients}
              onClose={() => setShowCat(false)}
            />
          )}
          {!artAdd && !bulk && (
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <div
                onClick={() => {
                  setArtNew((p) => ({ ...p, cat: selCat || p.cat || '' }));
                  setArtAdd(true);
                }}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  border: '1px dashed ' + C.navy,
                  borderRadius: 9,
                  padding: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.navy,
                  cursor: 'pointer',
                }}
              >
                {selCat ? '＋ Ajouter dans ' + selCat : '＋ Nouvel article'}
              </div>
              <div
                onClick={() => setBulk(true)}
                style={{
                  flex: 1,
                  textAlign: 'center',
                  border: '1px dashed ' + C.teal,
                  borderRadius: 9,
                  padding: 10,
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.teal,
                  cursor: 'pointer',
                }}
              >
                📋 Ajout en lot · coller une liste
              </div>
            </div>
          )}
          {bulk && (
            <AjoutEnLot
              stock={stock}
              setStock={setStock}
              rayon={selCat}
              voitPrixAchat={voitPrixAchat}
              onFermer={() => setBulk(false)}
            />
          )}
          {artAdd && (
            <Card style={{ marginBottom: 12, border: '2px solid ' + C.navy }}>
              <Bar
                icon="📦"
                title="Nouvel article"
                color={C.navy}
                sub={
                  (estMixte ? univers === 'magasin' : soc?.secteur === 'magasin')
                    ? 'Ajoute un produit à ton stock'
                    : 'Choisis un métier pour pré-remplir, ou saisis directement'
                }
              />
              <div style={{ padding: 13 }}>
                {!(estMixte ? univers === 'magasin' : soc?.secteur === 'magasin') ? (
                  <>
                    <div
                      style={{
                        fontSize: 8.5,
                        fontWeight: 700,
                        color: C.muted,
                        marginBottom: 5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    >
                      ⚡ Présélection par métier
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
                      {TRADE_PRESETS.map((g) => (
                        <div
                          key={g.metier}
                          onClick={() => setPresetMet(presetMet === g.metier ? '' : g.metier)}
                          style={{
                            padding: '5px 9px',
                            borderRadius: 8,
                            fontSize: 9,
                            fontWeight: 700,
                            cursor: 'pointer',
                            border: '1px solid ' + (presetMet === g.metier ? C.navy : C.border),
                            background: presetMet === g.metier ? C.navy : C.white,
                            color: presetMet === g.metier ? '#fff' : C.grey,
                          }}
                        >
                          {tr(g.metier)}
                        </div>
                      ))}
                    </div>
                    {presetMet && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
                        {((TRADE_PRESETS.find((g) => g.metier === presetMet) || {}).arts || []).map(
                          (a, i) => (
                            <div
                              key={i}
                              onClick={() =>
                                setArtNew((p) => ({
                                  ...p,
                                  nom: tr(a.nom),
                                  cat: tr(a.cat),
                                  u: a.u,
                                  pv: a.pv || 0,
                                }))
                              }
                              style={{
                                padding: '5px 9px',
                                borderRadius: 8,
                                fontSize: 9,
                                fontWeight: 600,
                                cursor: 'pointer',
                                border: '1px dashed ' + C.teal,
                                background: C.tealL,
                                color: C.teal,
                              }}
                            >
                              ＋ {tr(a.nom)}
                            </div>
                          ),
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  (() => {
                    const dej = [
                      ...new Set([
                        ...stockTri.map((a) => a.cat).filter(Boolean),
                        ...metierRayons(soc, true),
                      ]),
                    ];
                    const defauts = [
                      'Divers',
                      'Promotions',
                      'Nouveautés',
                      'Matériel',
                      'Outillage',
                      'Consommables',
                      'Accessoires',
                    ];
                    const rayons = [...dej, ...defauts.filter((r) => !dej.includes(r))];
                    return (
                      <>
                        <div
                          style={{
                            fontSize: 8.5,
                            fontWeight: 700,
                            color: C.muted,
                            marginBottom: 5,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                          }}
                        >
                          🏷️ Rayon (catégorie du produit)
                        </div>
                        <div
                          style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}
                        >
                          {rayons.map((r) => (
                            <div
                              key={r}
                              onClick={() => setArtNew((p) => ({ ...p, cat: r }))}
                              style={{
                                padding: '5px 10px',
                                borderRadius: 8,
                                fontSize: 9,
                                fontWeight: 700,
                                cursor: 'pointer',
                                border: '1px solid ' + (artNew.cat === r ? C.navy : C.border),
                                background: artNew.cat === r ? C.navy : C.white,
                                color: artNew.cat === r ? '#fff' : C.grey,
                              }}
                            >
                              {r}
                            </div>
                          ))}
                          <div
                            onClick={async () => {
                              try {
                                const v = await demande(
                                  stockOnly
                                    ? 'Nom du nouveau corps de métier / rayon\n(ex. Menuiserie, Toiture, Jardinage, Serrurerie…) :'
                                    : 'Nom du nouveau rayon\n(ex. Outillage, Peinture, Quincaillerie, Jardin…) :',
                                );
                                if (v && v.trim()) setArtNew((p) => ({ ...p, cat: v.trim() }));
                              } catch (e) {}
                            }}
                            style={{
                              padding: '5px 10px',
                              borderRadius: 8,
                              fontSize: 9,
                              fontWeight: 800,
                              cursor: 'pointer',
                              border: '1.5px dashed ' + C.green,
                              background: '#fff',
                              color: C.green,
                            }}
                          >
                            {stockOnly ? '＋ Nouveau corps de métier' : '＋ Nouveau rayon'}
                          </div>
                        </div>
                      </>
                    );
                  })()
                )}
                <Inp
                  label="Nom"
                  value={artNew.nom}
                  onChange={(e) => setArtNew((p) => ({ ...p, nom: e.target.value }))}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Inp
                    label={'PA ' + DEVISE}
                    type="number"
                    value={artNew.pa}
                    onChange={(e) => setArtNew((p) => ({ ...p, pa: +e.target.value }))}
                  />
                  <Inp
                    label={'PV ' + DEVISE}
                    type="number"
                    value={artNew.pv}
                    onChange={(e) => setArtNew((p) => ({ ...p, pv: +e.target.value }))}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <Inp
                    label={'Stock (' + (artNew.u || 'pce') + ')'}
                    type="number"
                    value={artNew.stock}
                    onChange={(e) => setArtNew((p) => ({ ...p, stock: +e.target.value }))}
                  />
                  <Inp
                    label={'Alerte mini (' + (artNew.u || 'pce') + ')'}
                    type="number"
                    value={artNew.min}
                    onChange={(e) => setArtNew((p) => ({ ...p, min: +e.target.value }))}
                  />
                </div>
                <div style={{ fontSize: 8, color: C.muted, margin: '-4px 0 8px', lineHeight: 1.5 }}>
                  💡 « Stock » = la quantité que tu as dans l'unité <b>{artNew.u || 'pce'}</b>. Ex.
                  unité <b>ml</b> → stock en mètres ; unité <b>pce</b> → nombre de pièces.
                </div>
                {/* Le nom, les prix et le stock suffisent ; le reste attend
                    sous « Détails ». */}
                <button
                  type="button"
                  aria-expanded={artNewDetails}
                  onClick={() => setArtNewDetails((o) => !o)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 6,
                    padding: '9px 0',
                    marginBottom: 10,
                    borderRadius: 10,
                    border: '1px solid ' + C.border,
                    background: C.white,
                    color: C.navy,
                    fontSize: 11,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  <span>{tr('Détails (facultatif)')}</span>
                  <span aria-hidden="true">{artNewDetails ? '▴' : '▾'}</span>
                </button>
                {artNewDetails && (
                  <>
                    <Inp
                      label={stockOnly ? 'Catégorie / corps de métier' : 'Catégorie / rayon'}
                      value={artNew.cat}
                      onChange={(e) => setArtNew((p) => ({ ...p, cat: e.target.value }))}
                    />
                    <div
                      style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'flex-end' }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Inp
                          label="Code-barres (EAN) — optionnel"
                          value={artNew.codeBarre || ''}
                          onChange={(e) => setArtNew((p) => ({ ...p, codeBarre: e.target.value }))}
                          placeholder="ex : 7612345678901"
                        />
                      </div>
                      <ScanCodeBarres
                        compact
                        onCode={(code) => setArtNew((p) => ({ ...p, codeBarre: code }))}
                      />
                    </div>
                    <div style={{ marginBottom: 11 }}>
                      <div
                        style={{ fontSize: 8.5, fontWeight: 700, color: C.muted, marginBottom: 8 }}
                      >
                        📎 PHOTOS &amp; PLANS DE L’ARTICLE (visibles par le client)
                      </div>
                      {/*
                    Les pièces partent au serveur, jamais dans la fiche : celles
                    choisies ici attendent la création de l'article, puis y
                    sont déposées (voir « ✓ Ajouter »).
                  */}
                      <ArticleMedia enAttente={artNewMedias} onAttente={setArtNewMedias} />
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 8 }}>
                      <Inp
                        label="Unité"
                        value={artNew.u}
                        onChange={(e) => setArtNew((p) => ({ ...p, u: e.target.value }))}
                      />
                    </div>
                    <TauxTvaArticle
                      valeur={artNew.tva}
                      onChange={(tva) => setArtNew((p) => ({ ...p, tva }))}
                      soc={soc}
                    />
                    <div
                      style={{
                        display: 'flex',
                        gap: 4,
                        flexWrap: 'wrap',
                        marginTop: -2,
                        marginBottom: 10,
                      }}
                    >
                      {metierUnites(
                        soc,
                        estMixte ? univers === 'magasin' : soc?.secteur === 'magasin',
                      ).map((u) => (
                        <span
                          key={u}
                          onClick={() => setArtNew((p) => ({ ...p, u }))}
                          style={{
                            fontSize: 8.5,
                            fontWeight: 700,
                            background: C.navyXL,
                            color: C.navy,
                            borderRadius: 99,
                            padding: '4px 9px',
                            cursor: 'pointer',
                          }}
                        >
                          {u}
                        </span>
                      ))}
                    </div>
                    <div
                      style={{
                        background: C.navyXL,
                        borderRadius: 8,
                        padding: '9px 10px',
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{ fontSize: 8.5, color: C.muted, marginBottom: 6, lineHeight: 1.4 }}
                      >
                        📦 Conditionnements (facultatif) — plusieurs niveaux possibles. Ex. « Carton
                        = 24 bouteille », « Palette = 50 carton ». Les équivalences en{' '}
                        {artNew.u || 'u'} sont calculées automatiquement.
                      </div>
                      {(artNew.conds || []).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
                          {artNew.conds.map((pk, i) => {
                            const base = condBase(pk, artNew.conds, artNew.u || 'u');
                            const par = pk.par || artNew.u || 'u';
                            return (
                              <div
                                key={i}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 5,
                                  background: C.tealL,
                                  border: '1px solid ' + C.teal + '40',
                                  borderRadius: 7,
                                  padding: '3px 8px',
                                  fontSize: 9,
                                  fontWeight: 700,
                                  color: C.teal,
                                }}
                              >
                                {pk.nom} = {pk.qte} {par}
                                {par !== (artNew.u || 'u')
                                  ? ' (= ' +
                                    Math.round(base * 1000) / 1000 +
                                    ' ' +
                                    (artNew.u || 'u') +
                                    ')'
                                  : ''}
                                <span
                                  onClick={() =>
                                    setArtNew((p) => ({
                                      ...p,
                                      conds: p.conds.filter((_, j) => j !== i),
                                    }))
                                  }
                                  style={{ cursor: 'pointer', color: C.red, fontWeight: 900 }}
                                >
                                  ×
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 7 }}>
                        {COND_TYPES.map((c) => (
                          <div
                            key={c}
                            onClick={() => setArtNew((p) => ({ ...p, cond: c }))}
                            style={{
                              padding: '4px 8px',
                              borderRadius: 7,
                              fontSize: 8.5,
                              fontWeight: 700,
                              cursor: 'pointer',
                              border: '1px solid ' + (artNew.cond === c ? C.teal : C.border),
                              background: artNew.cond === c ? C.teal : C.white,
                              color: artNew.cond === c ? '#fff' : C.grey,
                            }}
                          >
                            {c}
                          </div>
                        ))}
                      </div>
                      <div
                        style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
                      >
                        <input
                          value={artNew.cond}
                          onChange={(e) => setArtNew((p) => ({ ...p, cond: e.target.value }))}
                          placeholder="Type (Carton…)"
                          style={{
                            flex: '1 1 90px',
                            minWidth: 70,
                            border: '1px solid ' + C.border,
                            borderRadius: 7,
                            padding: '6px 8px',
                            fontSize: 9.5,
                          }}
                        />
                        <span style={{ fontSize: 9, color: C.muted }}>=</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={artNew.condQte}
                          onChange={(e) => setArtNew((p) => ({ ...p, condQte: e.target.value }))}
                          placeholder="qté"
                          style={{
                            width: 52,
                            border: '1px solid ' + C.border,
                            borderRadius: 7,
                            padding: '6px 8px',
                            fontSize: 9.5,
                            textAlign: 'center',
                          }}
                        />
                        <select
                          value={artNew.condPar || artNew.u || 'u'}
                          onChange={(e) => setArtNew((p) => ({ ...p, condPar: e.target.value }))}
                          style={{
                            flex: '1 1 90px',
                            minWidth: 80,
                            border: '1px solid ' + C.border,
                            borderRadius: 7,
                            padding: '6px 8px',
                            fontSize: 9.5,
                            background: '#fff',
                          }}
                        >
                          <option value={artNew.u || 'u'}>{artNew.u || 'u'} (base)</option>
                          {(artNew.conds || []).map((pk, i) => (
                            <option key={i} value={pk.nom}>
                              {pk.nom}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={{ marginTop: 7 }}>
                        <Btn
                          label="+ Ajouter ce conditionnement"
                          sm
                          ghost
                          color={C.teal}
                          full
                          disabled={
                            !artNew.cond.trim() ||
                            !(parseFloat((artNew.condQte || '').toString().replace(',', '.')) > 0)
                          }
                          onClick={() =>
                            setArtNew((p) => ({
                              ...p,
                              conds: [
                                ...(p.conds || []),
                                {
                                  nom: p.cond.trim(),
                                  qte:
                                    parseFloat((p.condQte || '').toString().replace(',', '.')) || 0,
                                  par: p.condPar || p.u || 'u',
                                },
                              ],
                              cond: '',
                              condQte: '',
                              condPar: '',
                            }))
                          }
                        />
                      </div>
                    </div>
                  </>
                )}
                {artNewErreur && (
                  <div role="alert" style={{ fontSize: 9, color: C.red, marginBottom: 8 }}>
                    {artNewErreur}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <div style={{ flex: 1 }}>
                    <Btn
                      label="✓ Ajouter"
                      sm
                      color={C.navy}
                      full
                      disabled={!artNew.nom || artNewEnvoi}
                      onClick={async () => {
                        setArtNewErreur('');
                        if (artNewMedias.length) {
                          // Des photos attendent : l'article est créé tout de
                          // suite, pour leur donner l'identifiant où se
                          // rattacher, puis la liste est relue.
                          setArtNewEnvoi(true);
                          try {
                            const dto = await stockApi.cree(
                              articleVersDto({
                                ...artNew,
                                id: idProvisoire(),
                                ref: prochaineReference('MAT', stock),
                              }),
                            );
                            for (const m of artNewMedias) {
                              await stockApi.deposeMedia(dto.id, m.fichier, m.typePiece, m.nom);
                            }
                            await rechargeStock?.();
                          } catch (e) {
                            setArtNewErreur(e?.message || 'L’enregistrement a échoué.');
                            return;
                          } finally {
                            setArtNewEnvoi(false);
                          }
                        } else {
                          // L'identifiant reste provisoire : le serveur seul en
                          // attribue de vrais, et un nombre fabrique ici le
                          // ferait prendre pour un article qu'il connait deja.
                          setStock((p) => [
                            ...p,
                            {
                              ...artNew,
                              id: idProvisoire(),
                              ref: prochaineReference('MAT', p),
                            },
                          ]);
                        }
                        setArtNewMedias([]);
                        setArtNew({
                          nom: '',
                          cat: '',
                          u: 'pce',
                          pv: 0,
                          pa: 0,
                          stock: 0,
                          min: 5,
                          cond: '',
                          condQte: '',
                          condPar: '',
                          conds: [],
                          codeBarre: '',
                        });
                        setArtAdd(false);
                      }}
                    />
                  </div>
                  <Btn
                    label="Annuler"
                    sm
                    ghost
                    color={C.grey}
                    onClick={() => {
                      setArtNewMedias([]);
                      setArtAdd(false);
                    }}
                  />
                </div>
              </div>
            </Card>
          )}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 7,
              paddingBottom: 6,
              marginBottom: 11,
            }}
          >
            <div
              onClick={() => setSelCat('')}
              style={{
                flex: '0 0 auto',
                whiteSpace: 'nowrap',
                padding: '8px 13px',
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 700,
                cursor: 'pointer',
                border: '1px solid ' + (selCat === '' ? C.navy : C.border),
                background: selCat === '' ? C.navy : C.white,
                color: selCat === '' ? '#fff' : C.grey,
              }}
            >
              Toutes
            </div>
            {/* Les articles sans rayon restent sous « Toutes » : leur pastille
                vide paraissait choisie en même temps. */}
            {cats.filter(Boolean).map((cat) => (
              <div
                key={cat}
                onClick={() => setSelCat(cat)}
                style={{
                  flex: '0 0 auto',
                  whiteSpace: 'nowrap',
                  padding: '8px 13px',
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: '1px solid ' + (selCat === cat ? C.navy : C.border),
                  background: selCat === cat ? C.navy : C.white,
                  color: selCat === cat ? '#fff' : C.grey,
                }}
              >
                {cat}
              </div>
            ))}
          </div>
          {selCat && (
            <div
              style={{
                background: C.navyXL,
                border: '1px solid ' + C.navy,
                borderRadius: 9,
                padding: '8px 11px',
                marginBottom: 10,
                fontSize: 9.5,
                color: C.navy,
                fontWeight: 700,
              }}
            >
              Corps de métier sélectionné : <b>{selCat}</b> — le bouton « ＋ Ajouter dans {selCat} »
              (en haut) ajoute directement ici.
            </div>
          )}
          {catsShown.map((cat) => (
            <div key={cat} style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: C.grey,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                {cat || tr('Divers')}
              </div>
              {stockTri
                .filter((m) => m.cat === cat)
                .map((art) => {
                  const bas = (+art.min || 0) > 0 && (+art.stock || 0) <= +art.min;
                  const mg = art.pv - art.pa;
                  return (
                    <Card
                      key={art.id}
                      style={{ marginBottom: 7, border: '1px solid ' + (bas ? C.amber : C.border) }}
                    >
                      <div style={{ padding: '12px 13px' }}>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: 8,
                            gap: 10,
                          }}
                        >
                          {vignetteDe(art) && (
                            <img
                              src={adresseApi(vignetteDe(art))}
                              alt=""
                              style={{
                                width: 46,
                                height: 46,
                                objectFit: 'cover',
                                borderRadius: 8,
                                flexShrink: 0,
                                border: '1px solid ' + C.border,
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 10, fontWeight: 700 }}>{art.nom}</div>
                            <div style={{ fontSize: 8, color: C.muted }}>{art.ref}</div>
                          </div>
                          {bas && <Pill label="⚠ Stock bas" color={C.amber} size={7} />}
                        </div>
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4,1fr)',
                            gap: 5,
                            marginBottom: 8,
                          }}
                        >
                          {[
                            ['Stock', art.stock + ' ' + art.u, bas ? C.red : C.ink],
                            ['Min.', art.min + ' ' + art.u, C.muted],
                            ['PV', DEVISE + ' ' + fmt(art.pv), C.green],
                            ['Marge', DEVISE + ' ' + fmt(mg), C.teal],
                          ].map(([l, v, c]) => (
                            <div key={l} style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 6.5, color: C.muted }}>{l}</div>
                              <div style={{ fontSize: 9, fontWeight: 800, color: c }}>{v}</div>
                            </div>
                          ))}
                        </div>
                        {((art.conds && art.conds.length) || art.cond) && (
                          <div
                            style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}
                          >
                            {(art.conds && art.conds.length
                              ? art.conds
                              : [{ nom: art.cond, qte: art.condQte }]
                            )
                              .filter((pk) => pk && pk.nom)
                              .map((pk, i) => {
                                const par = pk.par || art.u;
                                const base = condBase(pk, art.conds, art.u);
                                return (
                                  <span
                                    key={i}
                                    style={{
                                      fontSize: 7.5,
                                      fontWeight: 700,
                                      color: C.teal,
                                      background: C.tealL,
                                      border: '1px solid ' + C.teal + '30',
                                      borderRadius: 6,
                                      padding: '2px 7px',
                                    }}
                                  >
                                    📦 {pk.nom} = {pk.qte} {par}
                                    {par !== art.u && base > 0
                                      ? ' (=' + Math.round(base * 1000) / 1000 + ' ' + art.u + ')'
                                      : ''}
                                  </span>
                                );
                              })}
                          </div>
                        )}
                        {/* Deux boutons par article : vendre (ou, sans caisse,
                            réapprovisionner) et « ⋯ » pour le reste. */}
                        <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end' }}>
                          <Btn
                            label="⋯"
                            sm
                            ghost
                            color={C.navy}
                            onClick={() => setOutilsArt(outilsArt === art.id ? null : art.id)}
                          />
                          {stockOnly ? (
                            <>
                              <Btn
                                label="📥 Achat"
                                sm
                                ghost
                                color={C.teal}
                                onClick={() => {
                                  setSortie(null);
                                  setReappro(
                                    reappro && reappro.id === art.id
                                      ? null
                                      : {
                                          id: art.id,
                                          qte: '',
                                          pa: art.pa || '',
                                          total: '',
                                          facture: '',
                                          fournisseur: '',
                                        },
                                  );
                                }}
                              />
                            </>
                          ) : null}
                          {!stockOnly && (
                            <Btn
                              label="Vendre"
                              sm
                              color={C.green}
                              onClick={() => {
                                setTab('ventes');
                                setShowVente(true);
                                setPanier((pp) =>
                                  pp.find((x) => x.artId === art.id)
                                    ? pp
                                    : [...pp, { artId: art.id, q: 1, rem: 0 }],
                                );
                              }}
                            />
                          )}
                        </div>
                        {outilsArt === art.id && (
                          <div
                            style={{
                              display: 'flex',
                              gap: 5,
                              justifyContent: 'flex-end',
                              flexWrap: 'wrap',
                              marginTop: 6,
                            }}
                          >
                            <Btn
                              label="✎"
                              sm
                              ghost
                              color={C.navy}
                              onClick={() =>
                                setArtEdit(artEdit && artEdit.id === art.id ? null : { ...art })
                              }
                            />
                            <Btn
                              label="− 1"
                              sm
                              ghost
                              color={C.navy}
                              onClick={() =>
                                setStock((p) =>
                                  p.map((m) =>
                                    m.id === art.id ? { ...m, stock: Math.max(0, m.stock - 1) } : m,
                                  ),
                                )
                              }
                            />
                            {!stockOnly && (
                              <>
                                <Btn
                                  label="📥 Achat"
                                  sm
                                  ghost
                                  color={C.teal}
                                  onClick={() => {
                                    setSortie(null);
                                    setReappro(
                                      reappro && reappro.id === art.id
                                        ? null
                                        : {
                                            id: art.id,
                                            qte: '',
                                            pa: art.pa || '',
                                            total: '',
                                            facture: '',
                                            fournisseur: '',
                                          },
                                    );
                                  }}
                                />
                              </>
                            )}
                            <Btn
                              label="📤 Sortie"
                              sm
                              ghost
                              color={C.amber}
                              onClick={() => {
                                setReappro(null);
                                setSortie(
                                  sortie && sortie.id === art.id
                                    ? null
                                    : { id: art.id, qte: '', motif: 'perte', note: '' },
                                );
                              }}
                            />
                          </div>
                        )}
                        {reappro && reappro.id === art.id && (
                          <div
                            style={{
                              marginTop: 10,
                              borderTop: '1px solid ' + C.border,
                              paddingTop: 10,
                              background: C.tealL,
                              borderRadius: 9,
                              padding: '11px 12px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9.5,
                                fontWeight: 800,
                                color: C.teal,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              📥 Entrée de stock (achat fournisseur)
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 7,
                                marginBottom: 7,
                              }}
                            >
                              <div>
                                <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                                  Quantité achetée ({art.u})
                                </div>
                                <input
                                  value={reappro.qte}
                                  onChange={(e) =>
                                    setReappro((p) => ({
                                      ...p,
                                      qte: e.target.value.replace(',', '.').replace(/[^\d.]/g, ''),
                                    }))
                                  }
                                  inputMode="decimal"
                                  placeholder="0"
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border: '1px solid ' + C.border,
                                    borderRadius: 8,
                                    padding: '8px',
                                    fontSize: 12,
                                  }}
                                />
                              </div>
                              <div>
                                <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                                  Total facture (TVA comprise) {DEVISE}
                                </div>
                                <input
                                  value={reappro.total}
                                  onChange={(e) =>
                                    setReappro((p) => ({
                                      ...p,
                                      total: e.target.value
                                        .replace(',', '.')
                                        .replace(/[^\d.]/g, ''),
                                      // Un total corrigé à la main : la TVA lue ne lui correspond plus.
                                      montantTva: null,
                                    }))
                                  }
                                  inputMode="decimal"
                                  placeholder="Ex : 3000"
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border: '1px solid ' + C.border,
                                    borderRadius: 8,
                                    padding: '8px',
                                    fontSize: 12,
                                  }}
                                />
                              </div>
                            </div>
                            <input
                              value={reappro.fournisseur}
                              onChange={(e) =>
                                setReappro((p) => ({ ...p, fournisseur: e.target.value }))
                              }
                              placeholder="Fournisseur (optionnel)"
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                border: '1px solid ' + C.border,
                                borderRadius: 8,
                                padding: '8px',
                                fontSize: 11,
                                marginBottom: 8,
                              }}
                            />
                            {+reappro.qte > 0 && +reappro.total > 0
                              ? (() => {
                                  // Le taux et la TVA de la facture quand ils sont lus ;
                                  // sinon le taux de l'article (2.6 % pour une denrée),
                                  // non celui de la caisse.
                                  const tx = reappro.tauxTva ?? tauxDeLArticle(art);
                                  const tvav =
                                    reappro.montantTva ??
                                    +reappro.total - +reappro.total / (1 + tx / 100);
                                  const htv = +reappro.total - tvav;
                                  const unitv = htv / +reappro.qte;
                                  return (
                                    <div
                                      style={{
                                        background: '#fff',
                                        border: '1px solid ' + C.teal,
                                        borderRadius: 8,
                                        padding: '8px 10px',
                                        marginBottom: 8,
                                        fontSize: 10,
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      <div>
                                        Prix unitaire (hors TVA) :{' '}
                                        <b>{DEVISE + ' ' + fmt(unitv)}</b> / {art.u}
                                      </div>
                                      <div>
                                        TVA récupérable ({tx}%) :{' '}
                                        <b style={{ color: C.teal }}>{DEVISE + ' ' + fmt(tvav)}</b>
                                      </div>
                                    </div>
                                  );
                                })()
                              : null}
                            <ScanFacture
                              label={
                                reappro.facture
                                  ? '📎 Facture scannée ✓'
                                  : '📷 Scanner / joindre la facture fournisseur'
                              }
                              color={reappro.facture ? C.green : C.teal}
                              style={{ marginBottom: 8 }}
                              onPhoto={async (data, fichier) => {
                                // Réduite comme un justificatif : la facture part avec
                                // la dépense, qui la gardait jusqu'ici sous un nom que
                                // le serveur ne lit pas — elle se perdait.
                                try {
                                  const j = fichier
                                    ? await prepareLeJustificatif(fichier)
                                    : { piece: data, pieceNom: 'facture', pieceMime: '' };
                                  setReappro((p) => ({
                                    ...p,
                                    facture: j.piece,
                                    factureNom: j.pieceNom,
                                    factureMime: j.pieceMime,
                                  }));
                                } catch (e) {
                                  window.alert(e.message);
                                }
                              }}
                              onLecture={(lu) => {
                                const a = achatDepuisFacture(lu, art, tauxCaisse);
                                // La TVA de la facture : son taux, et son montant quand la
                                // facture ne porte que cet article (même total).
                                const unTaux =
                                  (lu?.tauxTva || []).length === 1 ? +lu.tauxTva[0] : null;
                                const memeTotal =
                                  a.total != null &&
                                  lu?.montantTtc != null &&
                                  Math.abs(a.total - lu.montantTtc) < 0.02;
                                setReappro((p) => ({
                                  ...p,
                                  fournisseur: a.fournisseur ?? p.fournisseur,
                                  qte: a.qte != null ? String(a.qte) : p.qte,
                                  total: a.total != null ? String(a.total) : p.total,
                                  tauxTva: unTaux,
                                  montantTva:
                                    memeTotal && lu.montantTva != null ? +lu.montantTva : null,
                                }));
                                return a.trouve
                                  ? undefined
                                  : 'Article non retrouvé sur la facture : saisis la quantité et le total.';
                              }}
                            />
                            <div style={{ display: 'flex', gap: 6 }}>
                              <Btn
                                label="✓ Enregistrer l'achat"
                                sm
                                color={C.teal}
                                full
                                disabled={!(+reappro.qte > 0)}
                                onClick={() => {
                                  const q = +reappro.qte || 0;
                                  if (q <= 0) return;
                                  // Le prix d'achat hors TVA au taux de la facture, lu au
                                  // scan, sinon au taux de l'article.
                                  const tx = reappro.tauxTva ?? tauxDeLArticle(art);
                                  const totalTTC = +reappro.total || 0;
                                  const htA = totalTTC / (1 + tx / 100);
                                  const paU =
                                    totalTTC > 0
                                      ? Math.round((htA / q) * 100) / 100
                                      : +reappro.pa || art.pa || 0;
                                  setStock((p) =>
                                    p.map((m) => {
                                      if (m.id !== art.id) return m;
                                      const ancienQ = +m.stock || 0,
                                        ancienPA = +m.pa || 0;
                                      const newQ = ancienQ + q;
                                      const paMoyen =
                                        newQ > 0
                                          ? Math.round(
                                              ((ancienQ * ancienPA + q * paU) / newQ) * 100,
                                            ) / 100
                                          : paU;
                                      return {
                                        ...m,
                                        stock: newQ,
                                        pa: paMoyen,
                                        achats: [
                                          {
                                            date: today(),
                                            qte: q,
                                            pa: paU,
                                            fournisseur: reappro.fournisseur || '',
                                            total: totalTTC,
                                          },
                                          ...(m.achats || []),
                                        ],
                                      };
                                    }),
                                  );
                                  if (totalTTC > 0 && typeof setFrais === 'function')
                                    setFrais((p) => [
                                      {
                                        id: idProvisoire(),
                                        type: 'materiel',
                                        libelle:
                                          (reappro.fournisseur || 'Achat') + ' \u2014 ' + art.nom,
                                        montant: totalTTC,
                                        // La TVA imprimée sur la facture se récupère telle quelle ;
                                        // sans elle, au taux de l'article acheté.
                                        tva: reappro.tauxTva ?? tauxDeLArticle(art),
                                        fournisseur: reappro.fournisseur || '',
                                        montantTva: reappro.montantTva ?? null,
                                        date: today(),
                                        photo: reappro.facture || '',
                                        piece: reappro.facture || '',
                                        pieceNom: reappro.factureNom || '',
                                        pieceMime: reappro.factureMime || '',
                                      },
                                      ...(p || []),
                                    ]);
                                  setReappro(null);
                                }}
                              />
                              <Btn
                                label="Annuler"
                                sm
                                ghost
                                color={C.grey}
                                onClick={() => setReappro(null)}
                              />
                            </div>
                            {art.achats && art.achats.length > 0 && (
                              <div style={{ marginTop: 9 }}>
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontWeight: 700,
                                    color: C.muted,
                                    marginBottom: 4,
                                  }}
                                >
                                  Derniers achats :
                                </div>
                                {art.achats.slice(0, 3).map((h, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 8.5,
                                      color: C.grey,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <span>
                                      {h.date} · {h.qte} {art.u}
                                      {h.fournisseur ? ' · ' + h.fournisseur : ''}
                                    </span>
                                    {h.pa != null && (
                                      <span>
                                        {DEVISE} {fmt(h.pa)}/u
                                      </span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {sortie && sortie.id === art.id && (
                          <div
                            style={{
                              marginTop: 10,
                              borderTop: '1px solid ' + C.border,
                              paddingTop: 10,
                              background: C.amberL || '#FEF3E2',
                              borderRadius: 9,
                              padding: '11px 12px',
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9.5,
                                fontWeight: 800,
                                color: C.amber,
                                textTransform: 'uppercase',
                                letterSpacing: 0.4,
                                marginBottom: 8,
                              }}
                            >
                              📤 Sortie de stock
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: 7,
                                marginBottom: 7,
                              }}
                            >
                              <div>
                                <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                                  Quantité ({art.u})
                                </div>
                                <input
                                  value={sortie.qte}
                                  onChange={(e) =>
                                    setSortie((p) => ({
                                      ...p,
                                      qte: e.target.value.replace(',', '.').replace(/[^\d.]/g, ''),
                                    }))
                                  }
                                  inputMode="decimal"
                                  placeholder="0"
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border:
                                      '1px solid ' +
                                      ((+sortie.qte || 0) > (+art.stock || 0) ? C.red : C.border),
                                    borderRadius: 8,
                                    padding: '8px',
                                    fontSize: 12,
                                  }}
                                />
                                {(+sortie.qte || 0) > (+art.stock || 0) && (
                                  <div
                                    style={{
                                      fontSize: 8,
                                      color: C.red,
                                      fontWeight: 700,
                                      marginTop: 3,
                                    }}
                                  >
                                    ⚠ Stock : {art.stock || 0} {art.u}
                                  </div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontSize: 8, color: C.muted, marginBottom: 3 }}>
                                  Motif
                                </div>
                                <select
                                  value={sortie.motif}
                                  onChange={(e) =>
                                    setSortie((p) => ({ ...p, motif: e.target.value }))
                                  }
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border: '1px solid ' + C.border,
                                    borderRadius: 8,
                                    padding: '8px',
                                    fontSize: 11,
                                    background: '#fff',
                                  }}
                                >
                                  {[
                                    'vente',
                                    // Au magasin, pas de chantier : la sortie
                                    // part chez un client.
                                    ...((
                                      estMixte ? univers === 'magasin' : soc?.secteur === 'magasin'
                                    )
                                      ? []
                                      : ['chantier']),
                                    'perte',
                                    'casse',
                                    'inventaire',
                                    'correction',
                                    'retour',
                                    'autre',
                                  ].map((m) => (
                                    <option key={m} value={m}>
                                      {m.charAt(0).toUpperCase() + m.slice(1)}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            {/* Pour un chantier : lequel. Le matériel s'inscrit à son
                                coût, au prix d'achat ; sans chantier, la sortie
                                faisait baisser le stock sans laisser de dépense. */}
                            {sortie.motif === 'chantier' && (
                              <select
                                aria-label={tr('Chantier')}
                                value={sortie.chantierId || ''}
                                onChange={(e) =>
                                  setSortie((p) => ({ ...p, chantierId: e.target.value }))
                                }
                                style={{
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  border: '1px solid ' + (sortie.chantierId ? C.border : C.amber),
                                  borderRadius: 8,
                                  padding: '8px',
                                  fontSize: 11,
                                  background: '#fff',
                                  marginBottom: 8,
                                }}
                              >
                                <option value="">{tr('Choisis le chantier du matériel.')}</option>
                                {(chantiers || [])
                                  .filter((c) => typeof c.id === 'number')
                                  .map((c) => (
                                    <option key={c.id} value={c.id}>
                                      {c.nom}
                                    </option>
                                  ))}
                              </select>
                            )}
                            {sortie.erreur && (
                              <div style={{ fontSize: 9, color: C.red, marginBottom: 6 }}>
                                {sortie.erreur}
                              </div>
                            )}
                            <input
                              value={sortie.note}
                              onChange={(e) => setSortie((p) => ({ ...p, note: e.target.value }))}
                              placeholder="Note (optionnel)"
                              style={{
                                width: '100%',
                                boxSizing: 'border-box',
                                border: '1px solid ' + C.border,
                                borderRadius: 8,
                                padding: '8px',
                                fontSize: 11,
                                marginBottom: 8,
                              }}
                            />
                            <div style={{ display: 'flex', gap: 6 }}>
                              <Btn
                                label="✓ Enregistrer la sortie"
                                sm
                                color={C.amber}
                                full
                                disabled={
                                  !(+sortie.qte > 0) ||
                                  (sortie.motif === 'chantier' && !sortie.chantierId) ||
                                  !!sortie.envoi
                                }
                                onClick={async () => {
                                  const q = +sortie.qte || 0;
                                  if (q <= 0) return;
                                  // Vers un chantier : le serveur sort le stock et
                                  // inscrit le coût au chantier, au prix d'achat, en
                                  // une seule opération.
                                  if (sortie.motif === 'chantier' && typeof art.id === 'number') {
                                    setSortie((p) => ({ ...p, envoi: true, erreur: '' }));
                                    try {
                                      await chantiersApi.depot(+sortie.chantierId, {
                                        retour: false,
                                        lignes: [{ articleId: art.id, quantite: q }],
                                      });
                                    } catch (e) {
                                      setSortie((p) => ({
                                        ...p,
                                        envoi: false,
                                        erreur:
                                          '⚠️ ' + (e?.message || tr("L'enregistrement a échoué.")),
                                      }));
                                      return;
                                    }
                                    Promise.all([rechargeStock?.(), rechargeChantiers?.()]).catch(
                                      () => {},
                                    );
                                    setSortie(null);
                                    return;
                                  }
                                  const nowHM = () => {
                                    const h = new Date();
                                    return (
                                      String(h.getHours()).padStart(2, '0') +
                                      ':' +
                                      String(h.getMinutes()).padStart(2, '0')
                                    );
                                  };
                                  setStock((p) =>
                                    p.map((m) =>
                                      m.id === art.id
                                        ? {
                                            ...m,
                                            stock: Math.max(0, (+m.stock || 0) - q),
                                            sorties: [
                                              {
                                                id: uid(),
                                                qte: q,
                                                motif: sortie.motif,
                                                note: sortie.note || '',
                                                date: today(),
                                                heure: nowHM(),
                                              },
                                              ...(m.sorties || []),
                                            ],
                                          }
                                        : m,
                                    ),
                                  );
                                  setSortie(null);
                                }}
                              />
                              <Btn
                                label="Annuler"
                                sm
                                ghost
                                color={C.grey}
                                onClick={() => setSortie(null)}
                              />
                            </div>
                            {art.sorties && art.sorties.length > 0 && (
                              <div style={{ marginTop: 9 }}>
                                <div
                                  style={{
                                    fontSize: 8,
                                    fontWeight: 700,
                                    color: C.muted,
                                    marginBottom: 4,
                                  }}
                                >
                                  Dernières sorties :
                                </div>
                                {art.sorties.slice(0, 4).map((h, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      fontSize: 8.5,
                                      color: C.grey,
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                    }}
                                  >
                                    <span>
                                      {h.date}
                                      {h.heure ? ' ' + h.heure : ''} · {h.qte} {art.u}
                                    </span>
                                    <span style={{ fontWeight: 700, color: C.amber }}>
                                      {h.motif}
                                      {h.chantierNom ? ' · ' + h.chantierNom : ''}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                        {artEdit && artEdit.id === art.id && (
                          <div
                            style={{
                              marginTop: 10,
                              borderTop: '1px solid ' + C.border,
                              paddingTop: 10,
                            }}
                          >
                            <Inp
                              label="Nom"
                              value={artEdit.nom}
                              onChange={(e) => setArtEdit((p) => ({ ...p, nom: e.target.value }))}
                            />
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 6,
                                alignItems: 'flex-end',
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <Inp
                                  label="Code-barres (EAN)"
                                  value={artEdit.codeBarre || ''}
                                  onChange={(e) =>
                                    setArtEdit((p) => ({ ...p, codeBarre: e.target.value }))
                                  }
                                  placeholder="ex : 7612345678901"
                                />
                              </div>
                              <ScanCodeBarres
                                compact
                                onCode={(code) => setArtEdit((p) => ({ ...p, codeBarre: code }))}
                              />
                            </div>
                            <div
                              style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}
                            >
                              <Inp
                                label="Catégorie"
                                value={artEdit.cat}
                                onChange={(e) => setArtEdit((p) => ({ ...p, cat: e.target.value }))}
                              />
                              <Inp
                                label="Unité"
                                value={artEdit.u}
                                onChange={(e) => setArtEdit((p) => ({ ...p, u: e.target.value }))}
                              />
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                gap: 4,
                                flexWrap: 'wrap',
                                marginTop: -2,
                                marginBottom: 10,
                              }}
                            >
                              {metierUnites(
                                soc,
                                estMixte ? univers === 'magasin' : soc?.secteur === 'magasin',
                              ).map((u) => (
                                <span
                                  key={u}
                                  onClick={() => setArtEdit((p) => ({ ...p, u }))}
                                  style={{
                                    fontSize: 8.5,
                                    fontWeight: 700,
                                    background: C.navyXL,
                                    color: C.navy,
                                    borderRadius: 99,
                                    padding: '4px 9px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {u}
                                </span>
                              ))}
                            </div>
                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                gap: 8,
                              }}
                            >
                              <Inp
                                label={'PA ' + DEVISE}
                                type="number"
                                value={artEdit.pa}
                                onChange={(e) => setArtEdit((p) => ({ ...p, pa: +e.target.value }))}
                              />
                              <Inp
                                label={'PV ' + DEVISE}
                                type="number"
                                value={artEdit.pv}
                                onChange={(e) => setArtEdit((p) => ({ ...p, pv: +e.target.value }))}
                              />
                              <Inp
                                label={'Alerte mini (' + (artEdit.u || 'pce') + ')'}
                                type="number"
                                value={artEdit.min}
                                onChange={(e) =>
                                  setArtEdit((p) => ({ ...p, min: +e.target.value }))
                                }
                              />
                            </div>
                            <div style={{ marginBottom: 11 }}>
                              <div
                                style={{
                                  fontSize: 8.5,
                                  fontWeight: 700,
                                  color: C.muted,
                                  marginBottom: 8,
                                }}
                              >
                                📎 PHOTOS &amp; PLANS DE L’ARTICLE (visibles par le client)
                              </div>
                              {/*
                                Les pieces partent au serveur des le depot, sans
                                attendre l'enregistrement de la fiche : elles ne
                                voyagent pas avec elle et ne s'y perdent donc
                                pas non plus.
                              */}
                              <ArticleMedia
                                articleId={art.id}
                                medias={art.medias}
                                onChange={(medias) =>
                                  setStock((liste) =>
                                    liste.map((m) => (m.id === art.id ? { ...m, medias } : m)),
                                  )
                                }
                              />
                            </div>
                            <TauxTvaArticle
                              valeur={artEdit.tva}
                              onChange={(tva) => setArtEdit((p) => ({ ...p, tva }))}
                              soc={soc}
                            />
                            <div style={{ display: 'flex', gap: 6 }}>
                              <div style={{ flex: 1 }}>
                                <Btn
                                  label="✓ Enregistrer"
                                  sm
                                  color={C.green}
                                  full
                                  onClick={() => {
                                    setStock((p) =>
                                      p.map((m) => (m.id === art.id ? { ...m, ...artEdit } : m)),
                                    );
                                    setArtEdit(null);
                                  }}
                                />
                              </div>
                              <Btn
                                label="🗑 Supprimer"
                                sm
                                ghost
                                danger
                                color={C.red}
                                onClick={() => {
                                  // Un article supprime emporte son historique de
                                  // mouvements : la suppression ne se rattrape pas.
                                  if (
                                    !window.confirm(
                                      'Supprimer « ' +
                                        (art.nom || 'cet article') +
                                        ' » du stock ?\n\nCette action est définitive. Pour un article qui ne se vend plus, préférez mettre son stock à zéro.',
                                    )
                                  ) {
                                    return;
                                  }
                                  setStock((p) => p.filter((m) => m.id !== art.id));
                                  setArtEdit(null);
                                }}
                              />
                              <Btn
                                label="Fermer"
                                sm
                                ghost
                                color={C.grey}
                                onClick={() => setArtEdit(null)}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  );
                })}
            </div>
          ))}
          {!stockOnly && voitInventaire && (
            // En magasin, les quatre onglets de la maquette remplissent la
            // ligne : l'inventaire s'ouvre d'ici, sous la liste.
            <Btn
              label="📋 Inventaire (comptage du stock)"
              sm
              ghost
              color={C.navy}
              full
              onClick={() => setTab('inventaire')}
            />
          )}
          {/* Rarement utile et sans retour : en bas, pas en tête de la liste. */}
          {(stock || []).length > 0 && (
            <div style={{ marginTop: 18 }}>
              <Btn
                label="🗑 Vider le stock"
                sm
                ghost
                color={C.red}
                full
                onClick={() => {
                  if (window.confirm('Vider TOUT le stock ? Tous les articles seront supprimés.'))
                    setStock([]);
                }}
              />
            </div>
          )}
        </div>
      )}

      {tab === 'ventes' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
            <Btn
              label={showVente ? 'Fermer' : '+ Nouvelle vente'}
              sm
              color={C.green}
              onClick={() => setShowVente((s) => !s)}
            />
          </div>
          {(() => {
            const vToday = ventesDuJour;
            const PAYL = {
              especes: '💵 Espèces',
              carte: '💳 Carte',
              twint: '📱 TWINT',
              bon: '🎟 Bon d’achat',
              autre: 'Autre',
            };
            const z = clotureDuJour;
            const { tva: zTva, ttc: zTtc, tickets: zTickets, articles: zArt } = z;
            const byPay = z.parPaiement;
            const tvaTaux = tauxCaisse;
            return (
              <Card style={{ marginBottom: 11 }}>
                <Bar
                  icon="📊"
                  title="Caisse du jour"
                  color={C.navy}
                  sub={
                    today() +
                    ' · ' +
                    zTickets +
                    ' ticket' +
                    (zTickets > 1 ? 's' : '') +
                    (z.avoirs.nombre > 0
                      ? ' · ' + z.avoirs.nombre + ' avoir' + (z.avoirs.nombre > 1 ? 's' : '')
                      : '')
                  }
                />
                <div style={{ padding: 13 }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      background: C.navyXL,
                      borderRadius: 9,
                      padding: '10px 12px',
                      marginBottom: 8,
                    }}
                  >
                    <span style={{ fontSize: 11, fontWeight: 800 }}>Total encaissé</span>
                    <span style={{ fontSize: 15, fontWeight: 900, color: C.green }}>
                      {DEVISE} {fmt(zTtc)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {['especes', 'carte', 'twint', 'bon', 'autre']
                      .filter((k) => byPay[k])
                      .map((k) => (
                        <div
                          key={k}
                          style={{
                            flex: '1 1 45%',
                            border: '1px solid ' + C.border,
                            borderRadius: 8,
                            padding: '7px 9px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontSize: 9.5,
                          }}
                        >
                          <span>{PAYL[k]}</span>
                          <b>
                            {DEVISE} {fmt(byPay[k])}
                          </b>
                        </div>
                      ))}
                  </div>
                  <div style={{ fontSize: 8.5, color: C.muted, marginBottom: 9 }}>
                    {zArt} article(s) vendu(s) · TVA{' '}
                    {(z.ventilation.length > 0 ? z.ventilation.map((v) => v.taux) : [tvaTaux])
                      .map((t) => t + '%')
                      .join(' + ')}{' '}
                    incluse ({DEVISE} {fmt(zTva)})
                  </div>
                  <Btn
                    label="📊 Rapport Z (clôture du jour)"
                    color={C.navy}
                    full
                    disabled={vToday.length === 0}
                    onClick={() => setShowZ(true)}
                  />
                </div>
              </Card>
            );
          })()}
          {showZ &&
            (() => {
              const PAYL = MOYENS;
              const z = clotureDuJour;
              const { ht: zHt, tva: zTva, ttc: zTtc, tickets: zTickets, articles: zArt } = z;
              const byPay = z.parPaiement;
              const tvaTaux = tauxCaisse;
              return (
                <Card style={{ marginBottom: 11, border: '2px solid ' + C.navy }}>
                  <div style={{ padding: 13 }}>
                    <style>
                      {
                        '@media print{body *{visibility:hidden}#rapport-z,#rapport-z *{visibility:visible}#rapport-z{position:absolute;left:0;top:0;width:100%}.noprint-z{display:none!important}}'
                      }
                    </style>
                    <div
                      id="rapport-z"
                      style={{
                        fontFamily: 'monospace',
                        maxWidth: 320,
                        margin: '0 auto',
                        background: '#fff',
                        color: '#111',
                        padding: '12px 14px',
                        border: '1px solid ' + C.border,
                        borderRadius: 6,
                      }}
                    >
                      <div style={{ textAlign: 'center', fontWeight: 900, fontSize: 13 }}>
                        RAPPORT Z
                      </div>
                      <div style={{ textAlign: 'center', fontSize: 9, marginBottom: 8 }}>
                        {soc?.nom || SOC.nom} · {today()}
                      </div>
                      <div
                        style={{
                          borderTop: '1px dashed #999',
                          borderBottom: '1px dashed #999',
                          padding: '5px 0',
                          fontSize: 9.5,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Tickets</span>
                          <b>{zTickets}</b>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Articles vendus</span>
                          <b>{zArt}</b>
                        </div>
                        {/*
                          Un avoir se retranche du jour ou il est fait : la
                          caisse doit dire ce qu'elle a rendu, et non
                          seulement ce qu'elle a encaisse.
                        */}
                        {z.avoirs.nombre > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>Avoirs ({z.avoirs.nombre})</span>
                            <b>
                              {DEVISE} {fmt(z.avoirs.ttc)}
                            </b>
                          </div>
                        )}
                      </div>
                      <div style={{ padding: '6px 0', fontSize: 9.5 }}>
                        <div style={{ fontWeight: 800, marginBottom: 3 }}>
                          Par moyen de paiement
                        </div>
                        {['especes', 'carte', 'twint', 'bon', 'autre']
                          .filter((k) => byPay[k])
                          .map((k) => (
                            <div
                              key={k}
                              style={{ display: 'flex', justifyContent: 'space-between' }}
                            >
                              <span>{PAYL[k]}</span>
                              <span>
                                {DEVISE} {fmt(byPay[k])}
                              </span>
                            </div>
                          ))}
                      </div>
                      <div style={{ borderTop: '1px dashed #999', paddingTop: 6, fontSize: 9.5 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Sous-total HT</span>
                          <span>{fmt(zHt)}</span>
                        </div>
                        {/*
                          Une ligne par taux : c'est la ventilation que l'AFC
                          attend d'une cloture, et non une somme globale.
                          L'assiette exoneree a 0 % s'y lit avec son montant :
                          c'est une recette declaree, meme sans taxe.
                        */}
                        {(z.ventilation.length > 0
                          ? z.ventilation
                          : [{ taux: tvaTaux, ht: zHt, tva: zTva }]
                        ).map((v) => (
                          <div
                            key={v.taux}
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                          >
                            <span>
                              {libelleTaux(v)}
                              {z.ventilation.length > 1 || v.exonere ? (
                                <span style={{ color: '#666' }}> sur {fmt(v.ht)}</span>
                              ) : null}
                            </span>
                            <span>{fmt(v.tva)}</span>
                          </div>
                        ))}
                        {z.avoirs.nombre > 0 && (
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              color: '#b45309',
                            }}
                          >
                            <span>dont avoirs HT</span>
                            <span>{fmt(z.avoirs.ht)}</span>
                          </div>
                        )}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            fontWeight: 900,
                            fontSize: 12,
                            marginTop: 3,
                          }}
                        >
                          <span>TOTAL TTC</span>
                          <span>
                            {DEVISE} {fmt(zTtc)}
                          </span>
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', fontSize: 8, marginTop: 8 }}>
                        Clôture du {today()} à{' '}
                        {new Date().toLocaleTimeString('fr-CH', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                    <div className="noprint-z" style={{ display: 'flex', gap: 8, marginTop: 11 }}>
                      <Btn label="🖨 Imprimer le rapport Z" color={C.navy} full onClick={printDoc} />
                      <Btn label="Fermer" color={C.muted} onClick={() => setShowZ(false)} />
                    </div>
                  </div>
                </Card>
              );
            })()}
          {showVente &&
            (() => {
              const cl = clients.find((c) => c.id === vClient);
              const lignes = panier
                .map((it) => {
                  const a = stock.find((x) => x.id === it.artId);
                  if (!a) return null;
                  const rem = Math.max(it.rem || 0, remisePour(cl, a));
                  const net = auCentime(a.pv * it.q * (1 - rem / 100));
                  return { ...it, a, rem, net };
                })
                .filter(Boolean);
              const totalArt = auCentime(lignes.reduce((s, l) => s + l.net, 0));
              const gRem = parseFloat((vRemiseGlob + '').replace(',', '.')) || 0;
              // Les prix du catalogue sont hors taxe : la TVA s'ajoute au
              // moment d'encaisser, elle ne s'extrait pas d'un prix qui ne la
              // contient pas. Additionner les prix affiches et les declarer
              // « TVA comprise » revenait a l'offrir au client et a la devoir
              // quand meme — le taux normal de chaque vente, pris sur la marge.
              //
              // Chaque ligne porte le taux de son article — une caisse d'eau a
              // 2,6 % et un ustensile a 8,1 % se ventilent sur le meme ticket
              // — et ce taux est fige ici : une vente est une piece comptable,
              // et le rapport Z de l'an dernier doit montrer le taux d'alors.
              const tauxLigne = (l) => (l.a.tva != null ? +l.a.tva : tauxCaisse);
              const netsRemises = lignes.map((l) => auCentime(l.net * (1 - gRem / 100)));
              // Le ticket se calcule sur les lignes telles qu'elles seront
              // enregistrees, par la fonction meme de la cloture : ce que le
              // client paie, ce que le Z totalise et ce que le decompte declare
              // sortent des memes chiffres, et ne peuvent pas diverger d'un
              // centime. Calculer le ticket a part — au niveau du document —
              // puis enregistrer des lignes arrondies une a une les faisait
              // differer.
              const lignesVente = lignes.map((l, rang) => ({
                total: netsRemises[rang],
                tx: tauxLigne(l),
                q: l.q,
                pay: vPay,
              }));
              const totaux = clotureCaisse(lignesVente, tauxCaisse);
              const total = totaux.ht;
              const tvaVente = totaux.tva;
              const totalTtc = totaux.ttc;
              return (
                <Card style={{ marginBottom: 11, border: '2px solid ' + C.green }}>
                  <Bar
                    icon="🛒"
                    title="Nouvelle vente"
                    color={C.green}
                    sub="Remises client appliquées"
                  />
                  <div style={{ padding: 13 }}>
                    <Inp label="Client">
                      <select
                        value={vClient}
                        onChange={(e) => setVClient(+e.target.value)}
                        style={{
                          width: '100%',
                          border: '1px solid ' + C.border,
                          borderRadius: 9,
                          padding: '10px',
                          fontSize: 11,
                        }}
                      >
                        <option value={0}>Client de passage</option>
                        {clientsTri.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.nom}
                          </option>
                        ))}
                      </select>
                    </Inp>
                    {!!vClient && cl && (
                      <div
                        style={{ fontSize: 9.5, color: C.amber, fontWeight: 800, marginBottom: 8 }}
                      >
                        ⭐ Fidélité : {cl.points || 0} point{(cl.points || 0) > 1 ? 's' : ''}
                      </div>
                    )}
                    {/* Un seul champ : le nom, ou le code-barres de la douchette
                        (Entrée ajoute l'article), et l'appareil photo à côté. */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      <input
                        value={artQ}
                        onChange={(e) => setArtQ(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const q = artQ.trim();
                          if (!q) return;
                          if (stock.some((x) => (x.codeBarre || '').trim() === q)) {
                            ajouterParCode(q);
                            setArtQ('');
                            return;
                          }
                          const trouves = stock.filter((a) =>
                            a.nom.toLowerCase().includes(q.toLowerCase()),
                          );
                          if (trouves.length === 1) {
                            const a = trouves[0];
                            setPanier((p) =>
                              p.find((x) => x.artId === a.id)
                                ? p
                                : [...p, { artId: a.id, q: 1, rem: 0 }],
                            );
                            setArtQ('');
                          } else if (!trouves.length && /^\d+$/.test(q)) {
                            ajouterParCode(q);
                          }
                        }}
                        placeholder="🔎 Rechercher un article (nom ou code)…"
                        style={{ ...inpFS, flex: 1, width: 'auto', minWidth: 0 }}
                      />
                      <ScanCodeBarres compact onCode={ajouterParCode} />
                    </div>
                    {scanInconnu && (
                      <div
                        role="status"
                        style={{ fontSize: 11, color: C.red, fontWeight: 700, marginBottom: 8 }}
                      >
                        Aucun article avec le code-barres {scanInconnu}.
                      </div>
                    )}
                    {artQ && (
                      <div
                        style={{
                          border: '1px solid ' + C.border,
                          borderRadius: 9,
                          marginBottom: 8,
                          maxHeight: 160,
                          overflowY: 'auto',
                        }}
                      >
                        {stock
                          .filter(
                            (a) =>
                              a.nom.toLowerCase().includes(artQ.toLowerCase()) ||
                              (a.codeBarre || '').includes(artQ.trim()),
                          )
                          .slice(0, 6)
                          .map((a) => (
                            <div
                              key={a.id}
                              onClick={() => {
                                setPanier((p) =>
                                  p.find((x) => x.artId === a.id)
                                    ? p
                                    : [...p, { artId: a.id, q: 1, rem: 0 }],
                                );
                                setArtQ('');
                              }}
                              style={{
                                padding: '9px 11px',
                                borderBottom: '1px solid ' + C.border,
                                cursor: 'pointer',
                                display: 'flex',
                                justifyContent: 'space-between',
                              }}
                            >
                              <span style={{ fontSize: 10 }}>{a.nom}</span>
                              <span style={{ fontSize: 9, color: C.green }}>
                                {DEVISE} {fmt(a.pv)}
                              </span>
                            </div>
                          ))}
                      </div>
                    )}
                    {lignes.map((l) => (
                      <div
                        key={l.artId}
                        style={{
                          background: C.navyXL,
                          borderRadius: 9,
                          padding: '9px 11px',
                          marginBottom: 7,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontSize: 10, fontWeight: 700 }}>{l.a.nom}</span>
                          <button
                            onClick={() => setPanier((p) => p.filter((x) => x.artId !== l.artId))}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: C.red,
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 8, color: C.muted }}>Qté</span>
                          <NumInput
                            value={l.q}
                            onChange={(e) =>
                              setPanier((p) =>
                                p.map((x) =>
                                  x.artId === l.artId ? { ...x, q: +e.target.value || 1 } : x,
                                ),
                              )
                            }
                            style={{
                              width: 50,
                              border: '1px solid ' + C.border,
                              borderRadius: 7,
                              padding: '5px',
                              fontSize: 10,
                              textAlign: 'center',
                            }}
                          />
                          <span style={{ fontSize: 8, color: C.muted }}>Remise {l.rem}%</span>
                          <span style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 800 }}>
                            {DEVISE} {fmt(l.net)}
                          </span>
                        </div>
                        {(+l.q || 0) > (+(l.a && l.a.stock) || 0) && (
                          <div
                            style={{ fontSize: 11, color: C.red, fontWeight: 700, marginTop: 5 }}
                          >
                            ⚠ Stock disponible : {(l.a && l.a.stock) || 0} {l.a && l.a.u} — quantité
                            trop élevée
                          </div>
                        )}
                      </div>
                    ))}
                    {lignes.length > 0 && (
                      <>
                        <div
                          style={{
                            background: C.navy,
                            borderRadius: 9,
                            padding: '10px 13px',
                            marginBottom: 10,
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <div>
                            <span style={{ fontSize: 11, fontWeight: 800, color: '#fff' }}>
                              À encaisser
                            </span>
                            <div style={{ fontSize: 8.5, color: '#cbd5e1', marginTop: 2 }}>
                              {DEVISE} {fmt(total)} HT + {fmt(tvaVente)} de TVA{' '}
                              {(totaux.ventilation.length > 0
                                ? totaux.ventilation.map((d) => d.taux)
                                : [tauxCaisse]
                              )
                                .map((t) => String(t).replace('.', ',') + ' %')
                                .join(' + ')}
                            </div>
                          </div>
                          <span style={{ fontSize: 15, fontWeight: 900, color: '#fff' }}>
                            {DEVISE} {fmt(totalTtc)}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 10,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 700,
                              color: C.muted,
                              textTransform: 'uppercase',
                              letterSpacing: 0.4,
                            }}
                          >
                            Remise manuelle sur la vente
                          </span>
                          <NumInput
                            value={vRemiseGlob}
                            onChange={(e) => setVRemiseGlob(e.target.value)}
                            placeholder="0"
                            style={{
                              width: 54,
                              border: '1px solid ' + C.border,
                              borderRadius: 8,
                              padding: '7px',
                              fontSize: 11,
                              textAlign: 'center',
                            }}
                          />
                          <span style={{ fontSize: 11, fontWeight: 700, color: C.muted }}>%</span>
                          {gRem > 0 && (
                            <span
                              style={{
                                marginLeft: 'auto',
                                fontSize: 9.5,
                                color: C.amber,
                                fontWeight: 800,
                              }}
                            >
                              −{DEVISE} {fmt(totalArt - total)}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 8.5,
                            fontWeight: 800,
                            color: C.muted,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                            marginBottom: 6,
                          }}
                        >
                          Moyen de paiement
                        </div>
                        <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                          {[
                            ['especes', '💵 Espèces'],
                            ['carte', '💳 Carte'],
                            ['twint', '📱 TWINT'],
                            ['autre', 'Autre'],
                          ].map(([v, lb]) => (
                            <div
                              key={v}
                              onClick={() => setVPay(v)}
                              style={{
                                flex: 1,
                                textAlign: 'center',
                                padding: '9px 3px',
                                borderRadius: 9,
                                fontSize: 9,
                                fontWeight: 700,
                                cursor: 'pointer',
                                border: '1px solid ' + (vPay === v ? C.green : C.border),
                                background: vPay === v ? C.green : C.white,
                                color: vPay === v ? '#fff' : C.grey,
                              }}
                            >
                              {lb}
                            </div>
                          ))}
                        </div>
                        {vPay === 'especes' && (
                          <div
                            style={{
                              display: 'flex',
                              gap: 10,
                              alignItems: 'flex-end',
                              marginBottom: 10,
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <Inp
                                label={'Montant reçu (' + DEVISE + ')'}
                                type="number"
                                value={vRecu}
                                onChange={(e) => setVRecu(e.target.value)}
                                placeholder={fmt(totalTtc)}
                              />
                            </div>
                            <div style={{ flex: 1, textAlign: 'right', paddingBottom: 10 }}>
                              <div
                                style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}
                              >
                                Rendu
                              </div>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 900,
                                  color:
                                    (parseFloat((vRecu + '').replace(',', '.')) || 0) >= totalTtc
                                      ? C.green
                                      : C.muted,
                                }}
                              >
                                {DEVISE}{' '}
                                {fmt(
                                  Math.max(
                                    0,
                                    (parseFloat((vRecu + '').replace(',', '.')) || 0) - totalTtc,
                                  ),
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {lignes.some((l) => (+l.q || 0) > (+(l.a && l.a.stock) || 0)) && (
                      <div
                        style={{
                          background: C.redL,
                          border: '1px solid ' + C.red,
                          borderRadius: 9,
                          padding: '9px 11px',
                          marginBottom: 9,
                          fontSize: 9.5,
                          color: C.red,
                          fontWeight: 700,
                          lineHeight: 1.45,
                        }}
                      >
                        ⚠ Stock insuffisant sur un ou plusieurs articles. Ajuste les quantités ou
                        faites un réapprovisionnement (📥 Achat) avant d'encaisser.
                      </div>
                    )}
                    <Btn
                      label="✓ Encaisser & ticket"
                      color={C.green}
                      full
                      disabled={
                        lignes.length === 0 ||
                        lignes.some((l) => (+l.q || 0) > (+(l.a && l.a.stock) || 0))
                      }
                      onClick={() => {
                        // Le numero imprime sur le ticket est celui que ses
                        // lignes retiennent : l'historique le retrouve, et un
                        // avoir peut le citer.
                        const numTicket = numeroTicket('T');
                        // Le montant recu laisse vide n'a pas ete compte :
                        // le ticket imprimait « Recu 0.00 / Rendu 0.00 »
                        // pour un client qui avait bel et bien paye. Il ne
                        // porte alors ni l'un ni l'autre (voir TicketComptoir).
                        const saisieRecu = String(vRecu ?? '')
                          .trim()
                          .replace(',', '.');
                        const recu =
                          vPay === 'especes'
                            ? saisieRecu
                              ? parseFloat(saisieRecu) || 0
                              : null
                            : totalTtc;
                        const rendu = recu == null ? 0 : Math.max(0, recu - totalTtc);
                        // Le taux de tete du ticket : celui de la societe quand
                        // le panier est homogene, faute de quoi la ventilation
                        // le remplace.
                        const tvaTaux = tauxCaisse;
                        lignes.forEach((l, rang) => {
                          setVentes((p) => [
                            {
                              id: uid(),
                              // Le taux applique, fige au moment de la vente ;
                              // et le ticket, qui rassemble les lignes d'un
                              // meme passage en caisse.
                              tx: tauxLigne(l),
                              ticket: numTicket,
                              date: today(),
                              // Un client de passage n'a pas de fiche : envoyer
                              // zero faisait chercher au serveur un client qui
                              // n'existe pas, et la vente etait refusee. Le
                              // ticket sortait quand meme, mais rien n'etait
                              // enregistre — ni le mouvement de stock, ni le
                              // chiffre d'affaires, ni la TVA encaissee.
                              clientId: vClient || null,
                              ref: l.a.ref,
                              nom: l.a.nom,
                              u: l.a.u,
                              q: l.q,
                              pv: l.a.pv,
                              total: netsRemises[rang],
                              pay: vPay,
                            },
                            ...p,
                          ]);
                          setStock((p) =>
                            p.map((m) =>
                              m.id === l.artId ? { ...m, stock: Math.max(0, m.stock - l.q) } : m,
                            ),
                          );
                        });
                        const ptsGain = vClient ? Math.round(total) : 0;
                        if (vClient) {
                          setClients((p) =>
                            p.map((c) =>
                              c.id === vClient ? { ...c, points: (c.points || 0) + ptsGain } : c,
                            ),
                          );
                        }
                        setTicket({
                          num: numTicket,
                          date: today(),
                          heure: new Date().toLocaleTimeString('fr-CH', {
                            hour: '2-digit',
                            minute: '2-digit',
                          }),
                          clientNom: cl ? cl.nom : 'Client de passage',
                          lignes: lignes.map((l) => ({
                            nom: l.a.nom,
                            q: l.q,
                            pv: l.a.pv,
                            rem: l.rem,
                            net: l.net,
                          })),
                          remiseGlob: gRem,
                          totalArt,
                          total: totalTtc,
                          totalHt: total,
                          tvaTaux,
                          tva: tvaVente,
                          ht: total,
                          // La ventilation par taux : un panier qui mele une
                          // caisse d'eau a 2,6 % et un ustensile a 8,1 % doit
                          // les montrer separement, c'est ce que l'AFC attend
                          // d'un ticket.
                          ventilation: totaux.ventilation,
                          pay: vPay,
                          recu,
                          rendu,
                          pointsGain: ptsGain,
                          pointsSolde: vClient ? ((cl && cl.points) || 0) + ptsGain : null,
                        });
                        setPanier([]);
                        setShowVente(false);
                        setVRecu('');
                        setVRemiseGlob('');
                      }}
                    />
                  </div>
                </Card>
              );
            })()}
          {ticket && <TicketComptoir ticket={ticket} soc={soc} onFermer={() => setTicket(null)} />}
          {avoirDe && (
            <AvoirComptoir
              origine={avoirDe}
              ventes={ventes}
              tauxCaisse={tauxCaisse}
              enregistre={enregistreAvoir}
              onFermer={() => setAvoirDe(null)}
            />
          )}
          {/*
            Une vente s'enregistre ligne par ligne ; l'historique les
            rassemble sous leur ticket, pour retrouver ce que le client a en
            main — et lui faire un avoir dessus.
          */}
          {ticketsDe(ventes, tauxCaisse).map((t) => {
            const cl = clients.find((c) => c.id === t.clientId);
            const synchronise = t.lignes.every((l) => typeof l.id === 'number');
            const resteARendre =
              !t.avoir &&
              synchronise &&
              quantitesRestantes(t.lignes, ventes).some((r) => r.restante > 0);
            return (
              <Card
                key={t.ticket || t.lignes[0].id}
                style={{ marginBottom: 7, border: t.avoir ? '1px solid ' + C.amber : undefined }}
              >
                <div style={{ padding: '11px 13px' }} data-ticket={t.ticket || ''}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700 }}>
                        {t.avoir ? '↩ Avoir ' : 'Ticket '}
                        {t.ticket || ''}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted }}>
                        {t.date}
                        {cl ? ' · ' + cl.nom : ''}
                        {t.avoir && t.lignes[0].ticketOrigine
                          ? ' · sur ' + t.lignes[0].ticketOrigine
                          : ''}
                        {t.avoir && t.lignes[0].motif ? ' · ' + t.lignes[0].motif : ''}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 900,
                        color: t.avoir ? C.amber : C.green,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {DEVISE} {fmt(t.ttc)}
                    </div>
                  </div>
                  {t.lignes.map((v) => (
                    <div
                      key={v.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 11,
                        color: C.grey,
                        marginTop: 3,
                      }}
                    >
                      <span>
                        {v.q} {v.u} × {v.nom}
                      </span>
                      <span>{fmt(v.total)}</span>
                    </div>
                  ))}
                  {resteARendre && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 7 }}>
                      <Btn
                        label="↩ Faire un avoir"
                        sm
                        ghost
                        color={C.amber}
                        onClick={() => {
                          setTicket(null);
                          setAvoirDe(t);
                        }}
                      />
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {tab === 'marge' &&
        voitLeBenefice() &&
        (() => {
          // L'article de la vente : par sa référence, ou par son nom pour les
          // ventes plus anciennes sans référence. Sans lui, la vente comptait
          // à coût nul (100 % de marge) et l'article apparaissait deux fois.
          const nomSimple = (t) =>
            String(t || '')
              .trim()
              .toLowerCase();
          const articleDe = (v) =>
            stock.find(
              (m) => (v.ref && (m.ref === v.ref || String(m.id) === String(v.ref))) || false,
            ) || stock.find((m) => nomSimple(m.nom) && nomSimple(m.nom) === nomSimple(v.nom));
          const paDe = (v) => {
            const a = articleDe(v);
            return a ? +a.pa || 0 : 0;
          };
          const ca = ventes.reduce((s, v) => s + (+v.total || 0), 0);
          const cout = ventes.reduce((s, v) => s + paDe(v) * (+v.q || 0), 0);
          const marge = ca - cout;
          const margePct = ca > 0 ? Math.round((marge / ca) * 1000) / 10 : 0;
          const stockPA = stock.reduce((s, m) => s + (+m.stock || 0) * (+m.pa || 0), 0);
          const stockPV = stock.reduce((s, m) => s + (+m.stock || 0) * (+m.pv || 0), 0);
          // Marge par article (regroupé sur les ventes)
          const parArt = {};
          ventes.forEach((v) => {
            const art = articleDe(v);
            const k = art ? 'a' + art.id : 'n' + nomSimple(v.nom);
            if (!parArt[k]) parArt[k] = { nom: art?.nom || v.nom, q: 0, ca: 0, cout: 0 };
            parArt[k].q += +v.q || 0;
            parArt[k].ca += +v.total || 0;
            parArt[k].cout += paDe(v) * (+v.q || 0);
          });
          const arts = Object.values(parArt)
            .map((a) => ({
              ...a,
              marge: a.ca - a.cout,
              pct: a.ca > 0 ? Math.round(((a.ca - a.cout) / a.ca) * 100) : 0,
            }))
            .sort((a, b) => b.marge - a.marge);
          return (
            <div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    background: 'linear-gradient(135deg,' + C.green + ',#0A5C32)',
                    borderRadius: 12,
                    padding: '14px 13px',
                    color: '#fff',
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      opacity: 0.8,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Marge brute réalisée
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>
                    {DEVISE} {fmt(marge)}
                  </div>
                  <div style={{ fontSize: 10, opacity: 0.85, marginTop: 1 }}>
                    {margePct}% du chiffre d'affaires
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ background: C.navyXL, borderRadius: 10, padding: '9px 11px' }}>
                    <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                      Chiffre d'affaires (ventes)
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: C.green }}>
                      {DEVISE} {fmt(ca)}
                    </div>
                  </div>
                  <div style={{ background: C.navyXL, borderRadius: 10, padding: '9px 11px' }}>
                    <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                      Coût des marchandises vendues
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 900, color: C.red }}>
                      {DEVISE} {fmt(cout)}
                    </div>
                  </div>
                </div>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 8,
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    border: '1px solid ' + C.border,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                    Valeur du stock (au prix d'achat)
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.navy }}>
                    {DEVISE} {fmt(stockPA)}
                  </div>
                </div>
                <div
                  style={{
                    border: '1px solid ' + C.border,
                    borderRadius: 10,
                    padding: '10px 12px',
                  }}
                >
                  <div style={{ fontSize: 8, color: C.muted, textTransform: 'uppercase' }}>
                    Valeur du stock (au prix de vente)
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: C.teal }}>
                    {DEVISE} {fmt(stockPV)}
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>
                    marge potentielle : {DEVISE} {fmt(stockPV - stockPA)}
                  </div>
                </div>
              </div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: C.muted,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  marginBottom: 7,
                }}
              >
                📊 Marge par article (sur les ventes)
              </div>
              {arts.length === 0 ? (
                <div
                  style={{ fontSize: 10, color: C.muted, padding: '14px 0', textAlign: 'center' }}
                >
                  Aucune vente enregistrée pour l'instant. La marge s'affichera dès tes premières
                  ventes.
                </div>
              ) : (
                arts.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '9px 11px',
                      marginBottom: 6,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <div style={{ flex: 1, marginRight: 8 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700 }}>{a.nom}</div>
                      <div style={{ fontSize: 8.5, color: C.muted }}>
                        {a.q} vendus · CA {DEVISE} {fmt(a.ca)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 900,
                          color: a.marge >= 0 ? C.green : C.red,
                        }}
                      >
                        {DEVISE} {fmt(a.marge)}
                      </div>
                      <div style={{ fontSize: 8.5, color: C.teal, fontWeight: 700 }}>{a.pct}%</div>
                    </div>
                  </div>
                ))
              )}
              <div style={{ fontSize: 8, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                La marge = prix de vente − prix d'achat, sur les quantités réellement vendues.
                Renseigne le prix d'achat (PA) de chaque article pour un calcul juste.
              </div>
            </div>
          );
        })()}

      {tab === 'remises' &&
        (() => {
          const cl = clients.find((c) => c.id === remCli) || clientsTri[0];
          if (!cl)
            return (
              <div style={{ textAlign: 'center', color: C.muted, padding: 20 }}>Aucun client</div>
            );
          const rems = cl.remises || {};
          if (remCat) {
            const items = [
              ...stockTri.map((a) => ({
                key: a.ref,
                nom: a.nom,
                cat: a.cat || 'Stock',
                base: a.pv,
                u: a.u,
                tva: a.tva,
                // La photo distingue en rayon deux articles voisins : le
                // client la retrouve dans son catalogue, imprimé compris.
                img: vignetteDe(a),
              })),
              ...ordreDeSaisie(catalogue).map((a) => ({
                key: a.id,
                nom: a.nom,
                cat: a.metier || 'Prestations',
                base: a.p,
                u: a.u || 'forfait',
              })),
            ].filter((a) => a.key != null);
            const lignes = items.map((a) => {
              const r = remCatPublic
                ? 0
                : Object.prototype.hasOwnProperty.call(rems, a.key)
                  ? +rems[a.key] || 0
                  : cl.remiseGlobale || 0;
              // Sans taux propre, l'article suit celui de la société.
              const taux = a.tva != null ? +a.tva : tauxCaisse;
              const net = Math.round(a.base * (1 - r / 100) * 100) / 100;
              return {
                ...a,
                rem: r,
                taux,
                base: prixCatalogue(a.base, taux, catTTC),
                net: prixCatalogue(net, taux, catTTC),
              };
            });
            const mentionTaxe = catTTC ? 'TTC' : 'HT';
            const gr = {};
            lignes.forEach((a) => {
              (gr[a.cat] = gr[a.cat] || []).push(a);
            });
            return (
              <PleinEcran>
                <div
                  ref={remCatDefile}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    background: '#fff',
                    // Au-dessus de l'en-tête (posé dans le document), sous la
                    // visionneuse des PDF.
                    zIndex: 9000,
                    overflow: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                  }}
                >
                  <style>
                    {
                      '@media print{@page{size:A4;margin:14mm}body *{visibility:hidden}.cat-sheet,.cat-sheet *{visibility:visible}.cat-sheet{position:absolute;left:0;top:0;width:100%}.cat-noprint{display:none!important}tr{page-break-inside:avoid}}'
                    }
                  </style>
                  {/* La barre reste en haut pendant qu'on descend le catalogue,
                      sous l'heure et la batterie de l'iPhone, avec de grands
                      boutons : on revient en arrière d'où que l'on soit. */}
                  <div
                    className="cat-noprint"
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      background: C.navy,
                      padding: 'calc(10px + var(--st-haut)) 14px 10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <button
                      onClick={() => setRemCat(false)}
                      style={{
                        background: '#fff',
                        border: 'none',
                        color: C.navy,
                        borderRadius: 10,
                        padding: '10px 14px',
                        fontSize: 14,
                        fontWeight: 800,
                        cursor: 'pointer',
                      }}
                    >
                      ← Retour
                    </button>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button
                        onClick={() => setEnvoiCat(true)}
                        style={{
                          background: C.green,
                          border: 'none',
                          color: '#fff',
                          borderRadius: 10,
                          padding: '10px 13px',
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        ✉️ Envoyer
                      </button>
                      <button
                        // Le vrai PDF du catalogue, au prix public ou aux prix
                        // du client : l'impression de la page était refusée
                        // par l'iPhone.
                        onClick={() =>
                          remCatPublic || !Number.isInteger(cl.id)
                            ? ouvreDocument('/catalogue/document', { ttc: catTTC }).catch(
                                () => undefined,
                              )
                            : ouvreDocument(`/clients/${cl.id}/catalogue/document`, {
                                ttc: catTTC,
                              }).catch(() => undefined)
                        }
                        style={{
                          background: C.teal,
                          border: 'none',
                          color: '#fff',
                          borderRadius: 10,
                          padding: '10px 13px',
                          fontSize: 13,
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        📤 Partager / PDF
                      </button>
                    </div>
                  </div>
                  {/* Catalogue prix de base : le neutre, à n'importe quelle adresse ;
                    sinon celui de la société, à ses personnes. */}
                  {envoiCat && remCatPublic && (
                    <EnvoiCatalogueNeutre ttc={catTTC} onFermer={() => setEnvoiCat(false)} />
                  )}
                  {envoiCat && !remCatPublic && (
                    <EnvoiCatalogue
                      client={cl}
                      ttc={catTTC}
                      setClients={setClients}
                      onFermer={() => setEnvoiCat(false)}
                    />
                  )}
                  <div
                    className="cat-noprint"
                    style={{
                      background: C.navyXL,
                      borderBottom: '1px solid ' + C.border,
                      padding: '6px 14px',
                      fontSize: 9,
                      color: C.muted,
                      textAlign: 'center',
                    }}
                  >
                    « ✉️ Envoyer » → le directeur, les techniciens, les collaborateurs. « Partager /
                    PDF » → e-mail ou WhatsApp.
                  </div>
                  <div
                    className="cat-noprint"
                    role="switch"
                    aria-checked={catTTC}
                    tabIndex={0}
                    onClick={() => setCatTTC((v) => !v)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setCatTTC((v) => !v);
                      }
                    }}
                    style={{
                      padding: '9px 14px',
                      borderBottom: '1px solid ' + C.border,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      background: '#fff',
                    }}
                  >
                    <div
                      style={{
                        width: 40,
                        height: 22,
                        borderRadius: 999,
                        background: catTTC ? C.green : C.border,
                        position: 'relative',
                        flexShrink: 0,
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: 3,
                          left: catTTC ? 21 : 3,
                          width: 16,
                          height: 16,
                          borderRadius: 999,
                          background: '#fff',
                          boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.ink }}>
                      Afficher les prix TTC (TVA comprise)
                    </span>
                  </div>
                  <div
                    className="cat-sheet"
                    style={{ maxWidth: 720, margin: '0 auto', padding: '18px 16px', color: C.ink }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        marginBottom: 14,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 900, color: C.navy }}>
                          {soc?.nom || '—'}
                        </div>
                        <div style={{ fontSize: 9, color: C.muted }}>
                          {[soc?.adr, soc?.npa].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 13, fontWeight: 900 }}>
                          {remCatPublic ? 'Catalogue — prix de base' : 'Tarifs personnalisés'}
                        </div>
                        <div style={{ fontSize: 9, color: C.muted }}>{today()}</div>
                      </div>
                    </div>
                    {/*
                    La mention est ce que le client lit avant les chiffres. En
                    pied de page et en huit points, elle passait inapercue : un
                    client pouvait comprendre que les prix etaient definitifs,
                    et decouvrir la taxe sur la facture.
                  */}
                    <div
                      style={{
                        background: C.amberL,
                        border: '1px solid ' + C.amber,
                        borderRadius: 8,
                        padding: '9px 12px',
                        marginBottom: 12,
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: C.ink,
                        textAlign: 'center',
                      }}
                    >
                      {catTTC
                        ? `Prix TVA comprise — TVA ${String(tauxCaisse).replace('.', ',')} % incluse`
                        : `Prix hors TVA — TVA ${String(tauxCaisse).replace('.', ',')} % en sus`}
                      {lignes.some((a) => a.tva != null && +a.tva !== tauxCaisse) ? (
                        <div style={{ fontSize: 9, fontWeight: 600, marginTop: 3 }}>
                          sauf les articles au taux réduit ou exonérés, dont le taux est indiqué
                        </div>
                      ) : null}
                    </div>
                    {remCatPublic ? (
                      <div
                        style={{
                          background: C.navyXL,
                          borderRadius: 8,
                          padding: '8px 11px',
                          marginBottom: 12,
                          fontSize: 10.5,
                          color: C.muted,
                        }}
                      >
                        Nos articles et prix. Contactez-nous pour un compte et des conditions
                        personnalisées.
                      </div>
                    ) : (
                      <div
                        style={{
                          background: C.navyXL,
                          borderRadius: 8,
                          padding: '8px 11px',
                          marginBottom: 12,
                          fontSize: 11,
                        }}
                      >
                        Client : <b>{cl.nom || ''}</b>
                        {(cl.remiseGlobale || 0) > 0 ? (
                          <span style={{ color: C.muted }}>
                            {' '}
                            · remise générale {cl.remiseGlobale}%
                          </span>
                        ) : null}
                      </div>
                    )}
                    {Object.keys(gr).map((cat) => (
                      <div key={cat} style={{ marginBottom: 10 }}>
                        <div
                          style={{
                            fontSize: 9,
                            fontWeight: 800,
                            color: C.navy,
                            borderBottom: '2px solid ' + C.navy,
                            paddingBottom: 3,
                            marginBottom: 2,
                          }}
                        >
                          {cat}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                          <thead>
                            <tr style={{ fontSize: 8, color: C.muted, textAlign: 'right' }}>
                              <th
                                style={{ padding: '3px 4px', textAlign: 'left', fontWeight: 700 }}
                              >
                                Article
                              </th>
                              <th style={{ padding: '3px 4px', fontWeight: 700 }}>
                                Prix de base {mentionTaxe}
                              </th>
                              <th style={{ padding: '3px 4px', fontWeight: 700 }}>Remise</th>
                              <th style={{ padding: '3px 4px', fontWeight: 700 }}>
                                Prix net {mentionTaxe}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {gr[cat].map((a) => (
                              <tr key={a.key} style={{ borderBottom: '1px solid ' + C.border }}>
                                <td style={{ padding: '5px 4px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    {a.img ? (
                                      <img
                                        src={adresseApi(a.img)}
                                        alt=""
                                        style={{
                                          width: 40,
                                          height: 40,
                                          borderRadius: 8,
                                          objectFit: 'cover',
                                          flexShrink: 0,
                                          border: '1px solid ' + C.border,
                                        }}
                                      />
                                    ) : null}
                                    <span>{a.nom}</span>
                                  </div>
                                </td>
                                <td
                                  style={{
                                    padding: '5px 4px',
                                    textAlign: 'right',
                                    color: C.muted,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {DEVISE} {fmt(a.base)}/{a.u}
                                </td>
                                <td
                                  style={{
                                    padding: '5px 4px',
                                    textAlign: 'right',
                                    color: a.rem > 0 ? C.green : C.muted,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {a.rem > 0 ? '-' + a.rem + '%' : '—'}
                                </td>
                                <td
                                  style={{
                                    padding: '5px 4px',
                                    textAlign: 'right',
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {DEVISE} {fmt(a.net)}
                                  <div style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>
                                    {mentionTaxe}
                                    {a.tva != null && +a.tva !== tauxCaisse
                                      ? ` · TVA ${String(a.tva).replace('.', ',')} %`
                                      : ''}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                    {lignes.length === 0 && (
                      <div style={{ fontSize: 10, color: C.muted }}>Aucun article.</div>
                    )}
                    <div
                      style={{
                        fontSize: 8,
                        color: C.muted,
                        marginTop: 14,
                        borderTop: '1px solid ' + C.border,
                        paddingTop: 8,
                      }}
                    >
                      {remCatPublic ? 'Prix de base' : 'Prix nets pour ' + (cl.nom || 'ce client')},
                      {catTTC
                        ? ` TVA comprise (${String(tauxCaisse).replace('.', ',')} %, ou taux indiqué).`
                        : ` hors TVA ; TVA ${String(tauxCaisse).replace('.', ',')} % en sus.`}{' '}
                      Document généré le {today()}. Sous réserve de modification.
                    </div>
                  </div>
                  <BoutonHaut cible={remCatDefile} />
                </div>
              </PleinEcran>
            );
          }
          const q = (remQ || '').trim().toLowerCase();
          const allArts = [
            ...stockTri.map((a) => ({
              key: a.ref,
              nom: a.nom,
              cat: a.cat || 'Stock',
              base: a.pv,
              u: a.u,
              src: 'Stock',
            })),
            ...ordreDeSaisie(catalogue).map((a) => ({
              key: a.id,
              nom: a.nom,
              cat: a.metier || 'Prestations',
              base: a.p,
              u: a.u || 'forfait',
              src: 'Prestation',
            })),
          ];
          const vis = allArts.filter(
            (a) =>
              a.key != null &&
              (!q ||
                (a.nom || '').toLowerCase().includes(q) ||
                (a.cat || '').toLowerCase().includes(q)),
          );
          const groups = {};
          vis.forEach((a) => {
            const k = a.cat || 'Autres';
            (groups[k] = groups[k] || []).push(a);
          });
          // Les remises ne bougent que sur l'ordre du patron : les autres comptes
          // les voient sans pouvoir les changer (le serveur les garde aussi).
          const remisesDuPatron = ACCESS_ROLE === 'patron';
          const setRem = (ref, nr) => {
            if (!remisesDuPatron) return;
            const t = ('' + nr).trim();
            setClients((p) =>
              p.map((c) => {
                if (c.id !== cl.id) return c;
                const r = { ...(c.remises || {}) };
                if (t === '') {
                  delete r[ref];
                } else {
                  r[ref] = Math.max(
                    0,
                    Math.min(100, Math.round((parseFloat(t.replace(',', '.')) || 0) * 10) / 10),
                  );
                }
                return { ...c, remises: r };
              }),
            );
          };
          const setGen = (nr) => {
            if (!remisesDuPatron) return;
            const v = Math.max(
              0,
              Math.min(100, Math.round((parseFloat(('' + nr).replace(',', '.')) || 0) * 10) / 10),
            );
            setClients((p) => p.map((c) => (c.id === cl.id ? { ...c, remiseGlobale: v } : c)));
          };
          const bulk = async () => {
            if (!remisesDuPatron) return;
            try {
              const v = await demande('Remise en % à appliquer à TOUS les articles affichés :', '');
              if (v == null || ('' + v).trim() === '') return;
              const pct = Math.max(0, Math.min(100, parseFloat(('' + v).replace(',', '.')) || 0));
              setClients((p) =>
                p.map((c) => {
                  if (c.id !== cl.id) return c;
                  const r = { ...(c.remises || {}) };
                  vis.forEach((a) => {
                    r[a.key] = pct;
                  });
                  return { ...c, remises: r };
                }),
              );
            } catch (e) {}
          };
          return (
            <div>
              <Inp label="① Choisir le client">
                <select
                  value={remCli}
                  onChange={(e) => setRemCli(+e.target.value)}
                  style={{
                    width: '100%',
                    border: '1px solid ' + C.border,
                    borderRadius: 9,
                    padding: '10px',
                    fontSize: 11,
                  }}
                >
                  {clientsTri.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nom}
                    </option>
                  ))}
                </select>
              </Inp>
              <div style={{ marginTop: -4, marginBottom: 11 }}>
                {!remAddCli ? (
                  <Btn
                    label="➕ Nouveau client"
                    sm
                    ghost
                    color={C.green}
                    onClick={() => setRemAddCli(true)}
                  />
                ) : (
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input
                      value={remNewName}
                      onChange={(e) => setRemNewName(e.target.value)}
                      placeholder="Nom du nouveau client"
                      style={{
                        flex: 1,
                        border: '1px solid ' + C.border,
                        borderRadius: 9,
                        padding: '9px 11px',
                        fontSize: 12,
                      }}
                    />
                    <Btn
                      label="✓ Créer"
                      sm
                      color={C.green}
                      disabled={!remNewName.trim()}
                      onClick={() => {
                        const nid = idProvisoire();
                        setClients((p) => [
                          ...p,
                          {
                            id: nid,
                            nom: remNewName.trim(),
                            adr: '',
                            npa: '',
                            tel: '',
                            email: '',
                            remiseGlobale: 0,
                            remises: {},
                          },
                        ]);
                        setRemCli(nid);
                        setRemNewName('');
                        setRemAddCli(false);
                      }}
                    />
                    <Btn
                      label="Annuler"
                      sm
                      ghost
                      color={C.grey}
                      onClick={() => {
                        setRemAddCli(false);
                        setRemNewName('');
                      }}
                    />
                  </div>
                )}
              </div>
              <div
                style={{
                  background: C.tealL,
                  border: '1px solid ' + C.teal,
                  borderRadius: 10,
                  padding: '10px 12px',
                  marginBottom: 11,
                  fontSize: 9.5,
                  color: C.ink,
                  lineHeight: 1.55,
                }}
              >
                {!remisesDuPatron && (
                  <div style={{ fontWeight: 800, color: C.navy, marginBottom: 6 }}>
                    {tr(
                      '🔒 Seul le patron peut modifier les remises. Tu les vois telles qu’il les a réglées.',
                    )}
                  </div>
                )}
                <b>Comment faire :</b>
                <br />① Choisis le client ci-dessus.
                <br />② Dans la liste, tape la remise <b>%</b> sur les articles voulus.
                <br />③ C'est <b>enregistré tout seul</b> — ce client gardera ses remises.
                <br />④ Pour un autre client : reviens en haut et change le client.
              </div>
              <div
                onClick={() => {
                  setRemCatPublic(false);
                  setRemCat(true);
                }}
                style={{
                  background: 'linear-gradient(135deg,' + C.teal + ',' + C.navy + ')',
                  borderRadius: 13,
                  padding: '15px 16px',
                  marginBottom: 9,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 13,
                  cursor: 'pointer',
                  boxShadow: '0 6px 18px rgba(13,148,136,.30)',
                }}
              >
                <span
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 11,
                    background: 'rgba(255,255,255,.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    flexShrink: 0,
                  }}
                >
                  📤
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 900, color: '#fff' }}>
                    Envoyer le catalogue à ce client
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      color: 'rgba(255,255,255,.85)',
                      marginTop: 2,
                      lineHeight: 1.4,
                    }}
                  >
                    {cl.nom} verra tous les articles à SES prix (avec ses remises)
                  </div>
                </div>
                <span style={{ fontSize: 20, color: '#fff' }}>›</span>
              </div>
              <div
                onClick={() => {
                  setRemCatPublic(true);
                  setRemCat(true);
                }}
                style={{
                  background: '#fff',
                  border: '2px solid ' + C.navy,
                  borderRadius: 13,
                  padding: '13px 16px',
                  marginBottom: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 13,
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 10,
                    background: C.navyXL,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    flexShrink: 0,
                  }}
                >
                  📄
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 900, color: C.navy }}>
                    Catalogue prix de base (sans remise)
                  </div>
                  <div style={{ fontSize: 9, color: C.muted, marginTop: 2, lineHeight: 1.4 }}>
                    Prix publics — à envoyer à un prospect
                  </div>
                </div>
                <span style={{ fontSize: 18, color: C.navy }}>›</span>
              </div>
              <Card style={{ marginBottom: 11 }}>
                <div
                  style={{
                    background: 'linear-gradient(90deg,' + C.navy + ',' + C.navyM + ')',
                    padding: '12px 14px',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 800, color: '#fff' }}>{cl.nom}</div>
                  <div style={{ fontSize: 8.5, color: 'rgba(255,255,255,.55)' }}>
                    Remises par article · enregistrées et appliquées automatiquement sur devis et
                    factures
                  </div>
                </div>
                <div style={{ padding: '12px 14px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: C.navyXL,
                      borderRadius: 9,
                      padding: '9px 11px',
                      marginBottom: 10,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: C.navy }}>
                        Remise générale
                      </div>
                      <div style={{ fontSize: 8, color: C.muted }}>
                        Utilisée si l'article n'a pas de remise propre
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <NumInput
                        value={cl.remiseGlobale || 0}
                        disabled={!remisesDuPatron}
                        onChange={(e) => setGen(e.target.value)}
                        style={{
                          width: 54,
                          border: '1px solid ' + C.border,
                          borderRadius: 7,
                          padding: '6px',
                          fontSize: 11,
                          textAlign: 'center',
                        }}
                      />
                      <span style={{ fontSize: 10, color: C.muted }}>%</span>
                    </div>
                  </div>
                  {/* Le délai négocié vaut pour toutes les factures du client. */}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: C.navyXL,
                      borderRadius: 9,
                      padding: '9px 11px',
                      marginBottom: 10,
                      flexWrap: 'wrap',
                      gap: 6,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: C.navy }}>
                        Délai de paiement
                      </div>
                      <div style={{ fontSize: 8, color: C.muted }}>
                        Échéance des factures de ce client
                      </div>
                    </div>
                    <select
                      aria-label="Délai de paiement"
                      value={
                        delaiSaisi(cl.delaiPaiement) === ''
                          ? ''
                          : String(delaiSaisi(cl.delaiPaiement))
                      }
                      onChange={(e) => {
                        const delai = delaiSaisi(e.target.value);
                        setClients((p) =>
                          p.map((c) => (c.id === cl.id ? { ...c, delaiPaiement: delai } : c)),
                        );
                      }}
                      style={{
                        border: '1px solid ' + C.border,
                        borderRadius: 7,
                        padding: '6px 8px',
                        fontSize: 10.5,
                        fontWeight: 700,
                        color: C.ink,
                        background: '#fff',
                      }}
                    >
                      <option value="">Par défaut</option>
                      {[
                        ...DELAIS_PROPOSES,
                        ...(delaiSaisi(cl.delaiPaiement) !== '' &&
                        !DELAIS_PROPOSES.includes(delaiSaisi(cl.delaiPaiement))
                          ? [delaiSaisi(cl.delaiPaiement)]
                          : []),
                      ].map((jours) => (
                        <option key={jours} value={String(jours)}>
                          {libelleDelai(jours)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    value={remQ}
                    onChange={(e) => setRemQ(e.target.value)}
                    placeholder="🔎 Filtrer un article…"
                    style={{
                      width: '100%',
                      border: '1px solid ' + C.border,
                      borderRadius: 9,
                      padding: '9px 11px',
                      fontSize: 12,
                      marginBottom: 8,
                    }}
                  />
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 6,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 8,
                        fontWeight: 800,
                        color: C.muted,
                        textTransform: 'uppercase',
                      }}
                    >
                      Remise par article (%)
                    </div>
                    {remisesDuPatron && (
                      <Btn label="Remise à tous" sm ghost color={C.teal} onClick={bulk} />
                    )}
                  </div>
                  {Object.keys(groups).map((cat) => (
                    <div key={cat}>
                      <div
                        style={{
                          fontSize: 8,
                          fontWeight: 800,
                          color: C.muted,
                          background: '#f6f7f9',
                          padding: '5px 8px',
                          borderRadius: 6,
                          margin: '7px 0 4px',
                        }}
                      >
                        {cat}
                      </div>
                      {groups[cat].map((art) => {
                        const has = Object.prototype.hasOwnProperty.call(rems, art.key);
                        const rem = has ? rems[art.key] || 0 : cl.remiseGlobale || 0;
                        const prix = Math.round(art.base * (1 - rem / 100) * 100) / 100;
                        return (
                          <div
                            key={art.src + art.key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '7px 0',
                              borderBottom: '1px solid ' + C.border,
                            }}
                          >
                            <div style={{ flex: 1, marginRight: 8, minWidth: 0 }}>
                              <div style={{ fontSize: 9.5, fontWeight: 600 }}>
                                {art.nom}{' '}
                                <span style={{ fontSize: 7, fontWeight: 700, color: C.muted }}>
                                  · {art.src}
                                </span>
                              </div>
                              <div style={{ fontSize: 7.5, color: C.muted }}>
                                Base {DEVISE} {fmt(art.base)}/{art.u} → {DEVISE} {fmt(prix)}
                                {has ? '' : cl.remiseGlobale ? ' (générale)' : ''}
                              </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                              <CaseRemise
                                valeur={has ? rems[art.key] : ''}
                                readOnly={!remisesDuPatron}
                                aria-label={art.nom}
                                onValide={(t) => setRem(art.key, t)}
                                // Sans remise générale, la case reste vide.
                                placeholder={cl.remiseGlobale ? String(cl.remiseGlobale) : ''}
                                style={{
                                  width: 46,
                                  border: '1px solid ' + (has ? C.teal : C.border),
                                  borderRadius: 7,
                                  padding: '5px',
                                  fontSize: 10,
                                  textAlign: 'center',
                                }}
                              />
                              <span style={{ fontSize: 8.5, color: C.muted }}>%</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                  {vis.length === 0 && (
                    <div style={{ fontSize: 9, color: C.muted, padding: '8px 0' }}>
                      Aucun article.
                    </div>
                  )}
                </div>
              </Card>
              <Btn
                label={
                  remSaved ? '✓ Remises enregistrées' : '💾 Enregistrer les remises de ' + cl.nom
                }
                color={remSaved ? C.green : C.navy}
                full
                onClick={() => {
                  setRemSaved(true);
                  try {
                    setTimeout(() => setRemSaved(false), 2500);
                  } catch (e) {}
                }}
              />
              {remSaved && (
                <div
                  style={{
                    fontSize: 9.5,
                    color: C.green,
                    fontWeight: 800,
                    textAlign: 'center',
                    marginTop: 7,
                  }}
                >
                  ✓ Les remises de {cl.nom} sont bien enregistrées.
                </div>
              )}
              <div
                style={{
                  fontSize: 8.5,
                  color: C.muted,
                  textAlign: 'center',
                  marginTop: 6,
                  lineHeight: 1.5,
                }}
              >
                Astuce : tes remises sont déjà sauvegardées au fur et à mesure. Ce bouton sert juste
                à confirmer.
              </div>
            </div>
          );
        })()}
    </Shell>
  );
}
